import XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getContactInfo, isNorwegianSite } from "@/utils/siteContext";
import { getBrand } from "@/utils/brand";
import { RISK_PROFILE, ALARM_SORT, normalizeRiskProfile, normalizeAlarmSort, riskProfileLabel, riskProfileTexts, customInstructionText, alarmSortLabel, alarmPlanLocale } from "@/utils/alarmEnums";
import { isPaymentTermsFreetextMarker } from "@/utils/paymentTerms";
import { isVirtualDeviceNoTest } from "@/utils/virtualDevice";

/**
 * Format a number with thousands separator (space for Swedish format)
 * @param {number|string} value - The value to format
 * @param {boolean} includeDecimals - Whether to include decimals (default: false)
 * @returns {string} Formatted number string
 */
function formatAmountWithSeparator(value, includeDecimals = false) {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  
  const options = {
    minimumFractionDigits: includeDecimals ? 2 : 0,
    maximumFractionDigits: includeDecimals ? 2 : 0
  };
  
  // Use Swedish locale which uses space as thousands separator
  return num.toLocaleString('sv-SE', options);
}

export default {
  // Expose formatAmountWithSeparator as a method
  formatAmount(value, includeDecimals = false) {
    return formatAmountWithSeparator(value, includeDecimals);
  },

  // Duplicate device identifiers (SEC-216): the server answers 409 with
  // { errors: { imei_number: { unit_name, type, ... }, ... } } when a saved
  // IMEI / serial / phone / inventory number already belongs to another
  // device. Shows one toast per conflicting field. Returns true if the
  // error was such a conflict (already handled), false otherwise.
  handleIdentifierConflict(error) {
    const data =
      error && error.response && error.response.status === 409 ? error.response.data : null;
    if (!data || !data.errors || typeof data.errors !== "object") return false;

    Object.keys(data.errors).forEach((field) => {
      this.handleError(null, this.identifierConflictMessage(field, data.errors[field]));
    });
    return true;
  },

  // The text for one duplicate identifier. The check covers every country
  // (2026-10-02), and a device in the other country is not visible on this
  // site - so then the message says where it is.
  identifierConflictMessage(field, info = {}) {
    const fieldKey = `alarm_identifier_errors.fields.${field}`;
    const fieldLabel = this.$te(fieldKey) ? this.$t(fieldKey) : field;
    const device = (info && (info.unit_name || info.type || info.alarm_id)) || "";
    const here = isNorwegianSite() ? "no" : "se";
    if (info && info.country && info.country !== here) {
      const country = this.$t(`alarm_identifier_errors.countries.${info.country}`);
      return this.$t("alarm_identifier_errors.in_use_other_country", { field: fieldLabel, device, country });
    }
    return this.$t("alarm_identifier_errors.in_use", { field: fieldLabel, device });
  },

  // ---- App users' virtual devices (utils/virtualDevice.js) ------------------

  // securcloud answers a save of an app user's device with
  // `virtual_ignored_fields` when it dropped a field the service owns
  // (VIRTUAL_OWNED_FIELDS) that the request tried to change. The device forms
  // send none of them, so in practice this never fires; it is here so that a
  // field securcloud takes over later is reported by name instead of
  // disappearing behind the success toast — the exact failure audit 3, item 3
  // describes.
  warnVirtualIgnoredFields(fields) {
    if (!Array.isArray(fields) || fields.length === 0) return;
    const labels = {
      unit_name: "alarm_view.name_of_product",
      alarm_central: "change_alarm_plan.alarm_center",
      agreement_number: "change_alarm_plan.agreement_number",
      seller: "change_alarm_plan.seller",
    };
    const names = fields.map((field) => {
      const key = labels[field] || `alarm_identifier_errors.fields.${field}`;
      return this.$te(key) ? this.$t(key) : field;
    });
    this.handleError(null, this.$t("alarm_info.virtual_ignored_fields", { fields: names.join(", ") }), "top-right", 10000);
  },

  // securcloud refused a test on an app user's device (409
  // virtual_device_no_test): say why rather than "something went wrong".
  // Returns false for any other error, so the usual handling still runs.
  handleVirtualDeviceNoTest(error) {
    if (!isVirtualDeviceNoTest(error)) return false;
    this.handleError(null, this.$t("alarm_info.virtual_device_no_test"));
    return true;
  },

  // ---- Safe-4 refusals (SEC-364 B1/B2) ----------------------------------------
  // When Safe-4 refuses a save, the server sends back 422 with what went wrong
  // (securecloud-server/integration/safe4/index.js, checkBeforeSave): either the
  // Safe-4 field rules (reason "invalid", a list of problems) or SAFECON itself
  // (reason "rejected", a KUIS code). Nothing was saved in either case. These
  // live here, next to handleIdentifierConflict, so every form that saves a
  // device explains it the same way.

  // Safe-4 refused the save, so NOTHING was saved. Say so, and mark the inputs
  // Safe-4 complained about when `observer` (the form's ValidationObserver) is
  // given. Returns false for any other error, so the usual handling still runs.
  handleSafe4Rejection(error, observer = null) {
    const response = error && error.response;
    if (!response || response.status !== 422 || !response.data || !response.data.safe4) return false;
    const data = response.data;

    if (observer && typeof observer.setErrors === "function") {
      const problems = data.reason === "invalid" && Array.isArray(data.errors) ? data.errors : [{ field: data.field }];
      const marks = {};
      problems.forEach((problem) => {
        const input = this.safe4InputFor(problem.field);
        if (input) marks[input] = [this.$t("safe4_form.rejected_mark")];
      });
      // vee-validate finds each input by its name or vid, which turns it red
      if (Object.keys(marks).length > 0) observer.setErrors(marks);
    }

    this.handleError(null, this.safe4RefusalText(data), "top-right", 10000);
    return true;
  },

  // What a Safe-4 refusal says, in words - also used by the bulk results list
  safe4RefusalText(data) {
    const body = data || {};
    if (body.reason === "invalid" && Array.isArray(body.errors)) {
      const problems = body.errors.map((problem) => `${this.safe4FieldLabel(problem.field)}: ${this.safe4RuleText(problem)}`).join("; ");
      return this.$t("safe4_form.invalid", { problems });
    }
    const code = body.errorCode || "?";
    const label = body.field ? this.safe4FieldLabel(body.field) : "";
    return label ? this.$t("safe4_form.rejected_field", { field: label, code }) : this.$t("safe4_form.rejected", { code });
  },

  // SEC-370: a business or department is only deleted once Safe-4 has
  // confirmed that every one of its devices is removed. The server answers 409
  // with the devices it could not confirm; this says which, or returns "" for
  // any other error.
  safe4OffboardBlockedText(error) {
    const data = error && error.response && error.response.data;
    if (!data || data.errorId !== "safe4_offboard_not_confirmed") return "";
    const devices = (data.blocked || data.data || []).map((d) => d.unit_name || d.alarm_id).filter(Boolean).join(", ");
    return this.$t("safe4_form.offboard_not_confirmed", { devices });
  },

  // SEC-385: after a customer or unit is saved, say what reached Safe-4: how
  // many subscribers were updated, how many are queued because Safe-4 is not
  // answering, and which devices were refused, by name and field. `safe4` is
  // the `safe4` part of the save's answer; without it nothing is shown and
  // false is returned, so the form shows its usual message instead.
  showSafe4SyncResult(safe4) {
    if (!safe4) return false;

    const parts = [this.$t("safe4_sync.saved")];
    if (safe4.synced) parts.push(this.$tc("safe4_sync.synced", safe4.synced, { count: safe4.synced }));
    if (safe4.queued) parts.push(this.$tc("safe4_sync.queued", safe4.queued, { count: safe4.queued }));

    const failed = safe4.failed || [];
    if (failed.length === 0) {
      this.handleSuccess(parts.join(" "), "top-right", 8000);
      return true;
    }

    // a refused device is named with the field Safe-4 refused, else its code
    const devices = failed.map((device) => {
      let reason = this.$t("safe4_sync.unknown_reason");
      if (device.field) reason = this.safe4FieldLabel(device.field);
      else if (device.errorCode) reason = this.$t("safe4_sync.code", { code: device.errorCode });
      return `${device.unit_name || device.alarm_id} (${reason})`;
    });
    parts.push(this.$tc("safe4_sync.failed", failed.length, { count: failed.length, devices: devices.join(", ") }));
    this.handleError(null, parts.join(" "), "top-right", 15000);
    return true;
  },

  // "contact_persons.1.phone" -> "Kontaktperson 2 – Nummer"
  safe4FieldLabel(path) {
    const parts = String(path || "").split(".");
    if (parts[0] === "contact_persons") {
      const row = parseInt(parts[1], 10);
      const keys = { name: "change_alarm_plan.name", function: "change_alarm_plan.function", phone: "change_alarm_plan.number" };
      const label = this.$t(keys[parts[parts.length - 1]] || "change_alarm_plan.number");
      return isNaN(row) ? label : `${this.$t("bulk_alarm_update.contact_row", { index: row + 1 })} – ${label}`;
    }
    const keys = {
      alarm_password: "change_alarm_plan.password",
      "from_address.street": "safe4_form.street",
      "from_address.zip": "safe4_form.postal_code",
    };
    return keys[path] ? this.$t(keys[path]) : String(path || "");
  },

  // The rule a field broke, in words
  safe4RuleText(problem) {
    if (problem.rule === "characters") return this.$t("change_alarm_plan.password_charset_error");
    if (problem.rule === "max") return this.$t("safe4_form.too_long", { max: problem.max });
    if (problem.rule === "postcode") return this.$t(problem.country === "se" ? "safe4_form.invalid_postcode_se" : "safe4_form.invalid_postcode");
    return problem.rule || "";
  },

  // The validation rules for a Safe-4 contact number (SEC-364 A9): digits only,
  // at most 20 characters, and the same number may not be used twice on one
  // alarm. Other alarm centrals keep the rules they always had. Shared by every
  // form with contact persons, so they cannot drift apart.
  //   contacts  every contact person on the alarm
  //   index     the row being validated
  //   isSafe4   Safe-4 is the selected alarm central
  //   required  true for the first row, which must be filled in
  safe4PhoneRules(contacts, index, isSafe4, required) {
    if (!isSafe4) return required ? "required" : "";

    // every other number on this alarm, so the rule can spot a repeat. Only its
    // digits and "+" go into the rule string: a stored "|" or "," would split it
    // (SEC-373), and the comparison ignores everything else anyway.
    const others = (contacts || [])
      .filter((contact, i) => i !== index)
      .map((contact) => String((contact && contact.phone) || "").replace(/[^0-9+]/g, ""))
      .filter((phone) => phone);

    const rules = required ? ["required"] : [];
    rules.push("max:20", "safe4_phone");
    if (others.length) rules.push("safe4_phone_unique:" + others.join(","));
    return rules.join("|");
  },

  // SEC-364 A16: KUIS has no fields for a personal alarm - the subscriber record
  // holds no transport, vehicle, colour or home/work address (WS-ins-new-hm-abon-req
  // in ns.wsdl) - so for Safe-4 that choice is not offered. Other centrals keep it.
  safe4AlarmSortChoices(options, isSafe4) {
    if (!isSafe4) return options;
    return (options || []).filter((option) => option.value !== ALARM_SORT.PERSONAL);
  },

  // The vee-validate name or vid of the input behind a field path. The contact
  // numbers and the password carry a vid for this; street and postcode use the
  // same field names in every form.
  safe4InputFor(path) {
    const field = String(path || "");
    const phone = field.match(/^contact_persons\.(\d+)\.phone$/);
    if (phone) return "contact-phone-" + phone[1];
    if (field === "alarm_password") return "alarm-password";
    if (field === "from_address.zip") return this.$t("safe4_form.postal_code");
    if (field === "from_address.street") return this.$t("safe4_form.street");
    return "";
  },

  handleError(error = null, message = "Något problem uppstod", position = "top-right", timeout = 5000) {
    if (message != "")
      this.$toasted.show(message, {
        type: "error",
        theme: "outline",
        position: position,
        duration: timeout,
        action: {
          text: "X",
          class: "font-sans",
          onClick: (e, toastObject) => {
            toastObject.goAway(0);
          },
        },
      });

    // if(process.env.VUE_APP_ENV === 'development' && error != null) console.error(error);
    console.log("error: ",error, "  message: ", message);

    if (error != null) {
      this.axios.post(`${process.env.VUE_APP_SERVER_URL}/frontend_error`, {
        message: error.message || "",
        stack: error.stack || "",
        url: this.$route.fullPath,
        username: this.$store.state.user ? this.$store.state.user.username : "",
        role: this.$store.state.user ? this.$store.state.user.role : "",
      });
    }
  },

  handleSuccess(message = null, position = "top-right", timeout = 5000) {
    const displayMessage = message || this.$t('common.action_completed');
    this.$toasted.show(displayMessage, {
      type: "success",
      theme: "outline",
      position: position,
      duration: timeout,
      action: {
        text: "X",
        class: "font-sans",
        onClick: (e, toastObject) => {
          toastObject.goAway(0);
        },
      },
    });
  },

  setPageTitle(title = "", routeName) {
    if (this.$route.name == routeName) this.$store.commit("SET_PAGE_TITLE", title);
  },

  formatDate(date) {
    return this.moment(date)
      .tz("Europe/Stockholm")
      .format("ddd, YYYY-MM-DD, HH:mm");
  },

  // Timestamp for Excel exports: Swedish local time as plain "YYYY-MM-DD HH:mm:ss".
  // Written as text, so Excel shows the same value on every computer and the
  // column sorts in time order (SEC-332).
  formatExportDateTime(date) {
    if (!date) return "";
    return this.moment(date)
      .tz("Europe/Stockholm")
      .format("YYYY-MM-DD HH:mm:ss");
  },

  excelExport(data, sheetName = "", fileName = "", format = "xlsx") {
    if (data.length) {
      let filtered = data.filter((el) => Object.keys(el).length);
      var columns = XLSX.utils.json_to_sheet(filtered);

      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, columns, sheetName);

      // Today's date for the file name, in Swedish local time. UTC gave
      // yesterday's date between midnight and 01:00/02:00 Swedish time (SEC-332).
      let this_date = this.moment().tz("Europe/Stockholm").format("YYYYMMDD");

      if (format === "csv") {
        // UTF-8 BOM so Excel opens å/ä/ö correctly in CSV files
        const csv = "\ufeff" + XLSX.utils.sheet_to_csv(columns);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${this_date}_${fileName}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        XLSX.writeFile(wb, `${this_date}_${fileName}.xlsx`);
      }
    } else {
      this.handleError(null, this.$t('alarm_pdf.no_data_to_export'), "top-right", 3000);
    }
  },

  // SEC-374: the name of a Safe-4 classification ('test', 'false' or 'confirmed').
  safe4ClassificationLabel(value) {
    if (!value) return "";
    const key = `safe4_classification.value_${value}`;
    if (this.$te(key)) return this.$t(key);
    return value;
  },

  // SEC-374: true when a log row has something from the Safe-4 link to show:
  // a classification, or at least the time the link was opened.
  hasSafe4Classification(classification) {
    if (!classification) return false;
    if (classification.value) return true;
    if (classification.opened_at) return true;
    return false;
  },

  // SEC-374: add the four Safe-4 columns to a SecurTool export.
  // exportRows are the rows written to the file, sourceRows the log rows they
  // were made from, in the same order. The columns are only added when at least
  // one row has Safe-4 data, so everyone else gets the same file as before.
  addSafe4ExportColumns(exportRows, sourceRows) {
    let anyRowHasData = false;
    for (const row of sourceRows) {
      if (this.hasSafe4Classification(row.alarm_classification)) {
        anyRowHasData = true;
        break;
      }
    }
    if (!anyRowHasData) return;

    for (let i = 0; i < exportRows.length; i++) {
      const classification = sourceRows[i].alarm_classification || {};
      const history = classification.history || [];

      // Earlier choices, oldest first, e.g. "Testlarm (2026-10-12 14:03:10)"
      const previous = [];
      for (const h of history) {
        previous.push(`${this.safe4ClassificationLabel(h.value)} (${this.formatExportDateTime(h.at)})`);
      }

      const exportRow = exportRows[i];
      exportRow[this.$t("safe4_classification.title")] = this.safe4ClassificationLabel(classification.value);
      exportRow[this.$t("safe4_classification.link_opened")] = this.formatExportDateTime(classification.opened_at);
      exportRow[this.$t("safe4_classification.classified")] = this.formatExportDateTime(classification.at);
      exportRow[this.$t("safe4_classification.previous")] = previous.join(", ");
    }
  },

  async getUser() {
    try {
      let response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/user`);
      this.$store.commit("SET_USER", response.data.data);
      this.$store.commit("SET_ADDONS", response.data.addons);
    } catch (error) {
      this.handleError(error);
    }
  },

  async refreshToken() {
    try {
      let data = { refresh_token: this.$store.state.refreshToken, username: this.$store.state.user.username };

      let response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/auth/refresh_token`, data);
      let { accessToken } = response.data;

      this.$store.commit("SET_TOKEN", accessToken);
      // todo: also change token in axios header

      return accessToken;
    } catch (error) {
      if (error.response && error.response.status == 403) {
        this.$store.dispatch("logout");
        // Import siteContext at the top of this file if not already imported
        const { getLoginUrl } = require("@/utils/siteContext");
        this.$router.push(getLoginUrl()).catch(() => {});
      }

      return null;
    }
  },

  generateGoogleMapActionDiv() {
    let controlDiv = document.createElement("div");
    controlDiv.className += "lg:hidden";

    const controlUI = document.createElement("div");
    controlUI.style.backgroundColor = "#1A8DE9";
    controlUI.style.border = "1px solid #1A8DE9";
    controlUI.style.borderRadius = "2px";
    controlUI.style.boxShadow = "0 2px 6px rgba(0,0,0,.3)";
    controlUI.style.cursor = "pointer";
    controlUI.style.marginTop = "10px";
    controlUI.style.marginBottom = "15px";
    controlUI.style.marginRight = "8px";
    controlUI.style.textAlign = "center";
    controlUI.title = "Click to open menu";
    controlDiv.appendChild(controlUI);

    const controlText = document.createElement("div");
    controlText.style.color = "#fff";
    controlText.style.fontFamily = "'Source Sans Pro', sans-serif";
    controlText.style.fontSize = "18px";
    controlText.style.fontWeight = "600";
    controlText.style.lineHeight = "38px";
    controlText.style.paddingLeft = "5px";
    controlText.style.paddingRight = "5px";
    controlText.innerHTML = "Alarm";
    controlUI.appendChild(controlText);

    return controlDiv;
  },

  zoomButton(direction) {
    let canvasContainer = this.$refs.canvasContainer;
    let containerWidth = canvasContainer.offsetWidth;

    let stage = this.$refs.stage.getStage();
    var scaleBy = 1.11;

    let oldScale = stage.scaleX();
    var newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

    let center = {
      x: stage.width() / 2,
      y: stage.height() / 2,
    };

    let relatedTo = {
      x: (center.x - stage.x()) / oldScale,
      y: (center.y - stage.y()) / oldScale,
    };

    this.configStage.scaleX = newScale;
    this.configStage.scaleY = newScale;

    let newPos = {
      x: center.x - relatedTo.x * newScale,
      y: center.y - relatedTo.y * newScale,
    };
    stage.position(newPos);

    if (containerWidth < 500) {
      this.configLabelGroup.scaleX = 0.4 / newScale;
      this.configLabelGroup.scaleY = 0.4 / newScale;
    } else {
      this.configLabelGroup.scaleX = 1 / newScale;
      this.configLabelGroup.scaleY = 1 / newScale;
    }
  },

  zoomWheel(e) {
    e.evt.preventDefault();

    let canvasContainer = this.$refs.canvasContainer;
    let containerWidth = canvasContainer.offsetWidth;

    let stage = this.$refs.stage.getStage();
    var scaleBy = 1.01;

    var oldScale = stage.scaleX();
    var pointer = stage.getPointerPosition();

    var mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    let direction = e.evt.deltaY > 0 ? 1 : -1;

    if (e.evt.ctrlKey) {
      direction = -direction;
    }

    var newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

    this.configStage.scaleX = newScale;
    this.configStage.scaleY = newScale;

    var newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    stage.position(newPos);

    if (containerWidth < 500) {
      this.configLabelGroup.scaleX = 0.4 / newScale;
      this.configLabelGroup.scaleY = 0.4 / newScale;
    } else {
      this.configLabelGroup.scaleX = 1 / newScale;
      this.configLabelGroup.scaleY = 1 / newScale;
    }
  },

  zoomTouch(e) {
    e.evt.preventDefault();

    let canvasContainer = this.$refs.canvasContainer;
    let containerWidth = canvasContainer.offsetWidth;

    var stage = this.$refs.stage.getStage();

    var touch1 = e.evt.touches[0];
    var touch2 = e.evt.touches[1];

    if (touch1 && touch2) {
      if (stage.isDragging()) {
        stage.stopDrag();
      }

      var p1 = {
        x: touch1.clientX,
        y: touch1.clientY,
      };
      var p2 = {
        x: touch2.clientX,
        y: touch2.clientY,
      };

      if (!this.lastCenter) {
        this.lastCenter = this.getCenter(p1, p2);
        return;
      }
      var newCenter = this.getCenter(p1, p2);

      var dist = this.getDistance(p1, p2);

      if (!this.lastDist) {
        this.lastDist = dist;
      }

      var pointTo = {
        x: (newCenter.x - stage.x()) / stage.scaleX(),
        y: (newCenter.y - stage.y()) / stage.scaleX(),
      };

      var scale = stage.scaleX() * (dist / this.lastDist);

      this.configStage.scaleX = scale;
      this.configStage.scaleY = scale;

      var dx = newCenter.x - this.lastCenter.x;
      var dy = newCenter.y - this.lastCenter.y;

      var newPos = {
        x: newCenter.x - pointTo.x * scale + dx,
        y: newCenter.y - pointTo.y * scale + dy,
      };

      stage.position(newPos);

      this.lastDist = dist;
      this.lastCenter = newCenter;

      if (containerWidth < 500) {
        this.configLabelGroup.scaleX = 0.4 / scale;
        this.configLabelGroup.scaleY = 0.4 / scale;
      } else {
        this.configLabelGroup.scaleX = 1 / scale;
        this.configLabelGroup.scaleY = 1 / scale;
      }
    }
  },

  zoomTouchEnd(e) {
    e.evt.preventDefault();
    this.lastDist = 0;
    this.lastCenter = null;
  },

  zoomReset() {
    let canvasContainer = this.$refs.canvasContainer;
    let containerWidth = canvasContainer.offsetWidth;

    let scale = containerWidth / this.imageSize.width;
    this.configStage.scaleX = scale;
    this.configStage.scaleY = scale;

    if (containerWidth < 500) {
      this.configLabelGroup.scaleX = 0.4 / scale;
      this.configLabelGroup.scaleY = 0.4 / scale;
    } else {
      this.configLabelGroup.scaleX = 1 / scale;
      this.configLabelGroup.scaleY = 1 / scale;
    }

    var stage = this.$refs.stage.getStage();
    stage.position({ x: 0, y: 0 });
  },

  getCenter(p1, p2) {
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
  },

  getDistance(p1, p2) {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  },

  calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
    var ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    return { width: srcWidth * ratio, height: srcHeight * ratio };
  },

  getCellStyle(highlight, newVal, oldVal) {
    let style = {};
    if (highlight && oldVal != newVal) style = { fillColor: "#ffd589" };
    return style;
  },

  // `unit`: the alarm's business (Unit). When it has a customer-specific
  // instruction enabled, that text replaces the standard risk profile text in
  // the "Åtgärd vid larm" section.
  // `customer`: the customer that business belongs to. A Dutch customer's
  // (country_origin 'nl') plan is written in Dutch.
  async generateAlarmPlanPDF({ alarm, customerName, sellers, oldAlarm, highlight, showImsiNumber, showDirectDispatch, unit, customer }) {
    // Get contact info based on site context (Norwegian or Swedish)
    const contact_info = getContactInfo();
    const isNorwegian = isNorwegianSite();
    // The site's language (Dutch for a Dutch customer), never the user's UI language
    const pdfLocale = alarmPlanLocale(customer);
    const t = (key) => this.$i18n.t(key, pdfLocale);

    let type = "seller",
      reseller_logo,
      company_name = contact_info.company_name,
      company_number = contact_info.company_number,
      company_address = contact_info.company_address,
      email = contact_info.email,
      phone = contact_info.phone,
      contact_person = contact_info.contact_person,
      contact_email = contact_info.contact_email,
      contact_phone = contact_info.contact_phone;

    let seller = _.find(sellers, { _id: alarm.seller });
    if (seller && (seller.type == "reseller" || seller.type == "alarm_center")) {
      type = "reseller";
      reseller_logo = seller.logo;
      company_name = seller.name;
      company_number = seller.company_number;
      company_address = `${seller.address}\n${seller.zip_code} ${seller.city}`;
      email = seller.email;
      phone = seller.phone;
      contact_person = seller.contact_person;
      contact_email = seller.contact_email;
      contact_phone = seller.contact_phone;
    }
    let whitelabel = false;
    if (this.$store.state.user.reseller_id) {
      try {
        let response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/reseller/whitelabel/${this.$store.state.user.reseller_id}`
        );
    
        let { whitelabel: isWhitelabel } = response.data; 

        if (isWhitelabel) {
          company_name = response.data.name;
          company_address = response.data.address;
          email = response.data.email;
          phone = response.data.phone;
          company_number = response.data.company_number;
          whitelabel = true;
        }
      } catch (error) {
        console.error("Error fetching reseller data:", error);
      }
    }

    // SEC-334: on a partner-brand address (CSG) the supplier block is the partner's,
    // whatever seller or reseller the alarm has. Securdia addresses are unchanged.
    const pdfBrand = getBrand();
    if (!pdfBrand.isDefault) {
      const brandContact = pdfBrand.contact;
      company_name = brandContact.company_name;
      company_number = brandContact.company_number;
      company_address = brandContact.company_address;
      email = brandContact.email;
      phone = brandContact.phone;
      contact_person = brandContact.contact_person;
      contact_email = brandContact.contact_email;
      contact_phone = brandContact.contact_phone;
      whitelabel = false;
    }

    let y = 0;
    let c = 1;

    var doc = new jsPDF();

    // Prevent autoTable from repeating header rows on page breaks
    const _origAutoTable = doc.autoTable.bind(doc);
    doc.autoTable = (options) => _origAutoTable({ showHead: 'firstPage', ...options });

    // header
    var logo = new Image();
    logo.crossOrigin = "anonymous";

    if (type == "reseller") logo.src = reseller_logo + `?time=${new Date().getTime()}`;
    else logo.src = require("@/assets/images/securdia_pdf_logo.png");
    if (!pdfBrand.isDefault) logo.src = pdfBrand.logos.pdf; // SEC-334: partner brand logo

    await logo.decode();

    let maxWidth = 300;
    let maxHeight = 80;
    let { width, height } = this.calculateAspectRatioFit(logo.width, logo.height, maxWidth, maxHeight);

    width = width * 0.264583; // to mm
    height = height * 0.264583; // to mm
    if (!whitelabel) {
      doc.addImage(logo, "PNG", 15, 5, width, height);
    }

    doc.setFontSize(12);
    doc.text(t("alarm_pdf.title"), 195, 14, { align: "right" });

    // supplier
    this.generateHeading(doc, y + 25, t("alarm_pdf.supplier"), c);
    c++;
    doc.setFontSize(11);

    doc.autoTable({
      startY: 50,
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.name"), t("alarm_pdf.org_number"), t("alarm_pdf.address")]],
      body: [[company_name, company_number, company_address]],
    });
    if (whitelabel) {
      doc.autoTable({
        headStyles: { fillColor: [62, 178, 222] },
        head: [[t("alarm_pdf.contact_company"), t("alarm_pdf.e_mail"), t("alarm_pdf.phone")]],
        body: [
          [company_name, email, phone],
        ],
      });
    } else if (isNorwegian) {
      // Norwegian site - only show company contact info (no separate contact person)
      doc.autoTable({
        theme: "grid",
        headStyles: { fillColor: [62, 178, 222] },
        head: [[t("alarm_pdf.contact_company"), t("alarm_pdf.e_mail"), t("alarm_pdf.phone")]],
        body: [
          [company_name, email, phone],
        ],
      });
    } else {
      // Swedish site - show both company and contact person
      doc.autoTable({
            theme: "grid",
            headStyles: { fillColor: [62, 178, 222] },
            head: [[t("alarm_pdf.contact_company"), t("alarm_pdf.e_mail"), t("alarm_pdf.phone")]],
            body: [
              [company_name, email, phone],
              [contact_person, contact_email, contact_phone],
            ],
          });
    }
    

    // alarm plan
    y = doc.lastAutoTable.finalY;
    this.generateHeading(doc, y, t("alarm_pdf.data_alarm_plan"), c);
    c++;
    y = y + 25;

    // customer name
    doc.autoTable({
      startY: y,
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.customer")]],
      body: [[customerName]],
    });

    y = doc.lastAutoTable.finalY + 7;

    // check which column height is greator
    let colMaxY = y;

    // left column
    doc.autoTable({
      startY: y,
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.business_name")]],
      body: [[{ content: alarm.alarm_unit_name, styles: this.getCellStyle(highlight, alarm.alarm_unit_name, oldAlarm.alarm_unit_name) }]],
      margin: { right: 107 },
    });
    doc.autoTable({
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.alerm_name")]],
      body: [[{ content: alarm.unit_name, styles: this.getCellStyle(highlight, alarm.unit_name, oldAlarm.unit_name) }]],
      margin: { right: 107 },
    });
    doc.autoTable({
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.number_of_users")]],
      body: [[{ content: alarm.user, styles: this.getCellStyle(highlight, alarm.user, oldAlarm.user) }]],
      styles: { overflow: "hidden" },
      margin: { right: 107 },
    });

    colMaxY = doc.lastAutoTable.finalY;

    // right column
    doc.autoTable({
      startY: y,
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.alarm_type")]],
      body: [[{ content: alarmSortLabel(t, alarm.selected_alarm_sort), styles: this.getCellStyle(highlight, alarmSortLabel(t, alarm.selected_alarm_sort), alarmSortLabel(t, oldAlarm.selected_alarm_sort)) }]],
      styles: { overflow: "hidden" },
      margin: { left: 107 },
    });
    doc.autoTable({
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.risk_profile")]],
      body: [[{ content: riskProfileLabel(t, alarm.risk_profile), styles: this.getCellStyle(highlight, riskProfileLabel(t, alarm.risk_profile), riskProfileLabel(t, oldAlarm.risk_profile)) }]],
      styles: { overflow: "hidden" },
      margin: { left: 107 },
    });
    if (normalizeRiskProfile(alarm.risk_profile) === RISK_PROFILE.ASSAULT || normalizeRiskProfile(alarm.risk_profile) === RISK_PROFILE.THREAT) {
      doc.autoTable({
        headStyles: { fillColor: [62, 178, 222] },
        head: [[t("alarm_pdf.reference_number")]],
        body: [[{ content: alarm.reference_number || "", styles: this.getCellStyle(highlight, alarm.reference_number, oldAlarm.reference_number) }]],
        styles: { overflow: "hidden" },
        margin: { left: 107 },
      });
    }
    doc.autoTable({
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.based_on_address")]],
      body: [[{ content: alarm.from_address.city, styles: this.getCellStyle(highlight, alarm.from_address.city, oldAlarm.from_address.city) }]],
      margin: { left: 107 },
    });

    if (doc.lastAutoTable.finalY > colMaxY) colMaxY = doc.lastAutoTable.finalY;
    // end column layout

    y = colMaxY;

    doc.autoTable({
      startY: y + 7,
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.work_assignment")]],
      body: [[{ content: alarm.business_description, styles: this.getCellStyle(highlight, alarm.business_description, oldAlarm.business_description) }]],
    });

    y = doc.lastAutoTable.finalY;
    if (y > 240) {
      doc.addPage();
      y = 10;
    }

    // personal alarm
    if (normalizeAlarmSort(alarm.selected_alarm_sort) === ALARM_SORT.PERSONAL) {
      this.generateHeading(doc, y, t("alarm_pdf.case_of_personal_alarm"), c);
      c++;

      // demo feedback: the person's free-text risk profile is gone from the
      // forms - the documentation's own Riskprofil above is the only one - so
      // the column it filled is no longer printed
      doc.autoTable({
        startY: y + 27,
        headStyles: { fillColor: [62, 178, 222] },
        head: [[t("alarm_pdf.person_name"), t("alarm_pdf.characteristics")]],
        body: [
          [
            { content: alarm.personal_alarm.name, styles: this.getCellStyle(highlight, alarm.personal_alarm.name, oldAlarm.personal_alarm.name) },
            { content: alarm.personal_alarm.reporting, styles: this.getCellStyle(highlight, alarm.personal_alarm.reporting, oldAlarm.personal_alarm.reporting) },
          ],
        ],
      });

      // demo feedback: the person's work address is gone from the forms - it
      // said the same as the alarm's own address - so its column is not
      // printed any more
      doc.autoTable({
        headStyles: { fillColor: [62, 178, 222] },
        head: [[t("alarm_pdf.home_address"), t("alarm_pdf.transport")]],
        body: [
          [
            { content: alarm.personal_alarm.home_address || "", styles: this.getCellStyle(highlight, alarm.personal_alarm.home_address, oldAlarm.personal_alarm.home_address) },
            { content: alarm.personal_alarm.transport ? t("alarm_create.transport_" + alarm.personal_alarm.transport) : "", styles: this.getCellStyle(highlight, alarm.personal_alarm.transport, oldAlarm.personal_alarm.transport) },
          ],
        ],
      });

      if (alarm.personal_alarm.transport === "car") {
        doc.autoTable({
          headStyles: { fillColor: [62, 178, 222] },
          head: [[t("alarm_pdf.registration_number"), t("alarm_pdf.model"), t("alarm_pdf.color")]],
          body: [
            [
              { content: alarm.personal_alarm.registration_number || "", styles: this.getCellStyle(highlight, alarm.personal_alarm.registration_number, oldAlarm.personal_alarm.registration_number) },
              { content: alarm.personal_alarm.model || "", styles: this.getCellStyle(highlight, alarm.personal_alarm.model, oldAlarm.personal_alarm.model) },
              { content: alarm.personal_alarm.color || "", styles: this.getCellStyle(highlight, alarm.personal_alarm.color, oldAlarm.personal_alarm.color) },
            ],
          ],
        });
      }

      y = doc.lastAutoTable.finalY;
      if (y > 240) {
        doc.addPage();
        y = 10;
      }
    }

    // alarm unit
    this.generateHeading(doc, y, t("alarm_pdf.alarm_unit"), c);
    c++;

    let alarmUnitCol = [t("alarm_pdf.alarm_number"), t("alarm_pdf.telematics_number"), t("alarm_pdf.imei"), t("alarm_pdf.device_type"), t("alarm_view.inventory_number")];
    let alarmUnitBody = [
      { content: alarm.alarm_number || "", styles: this.getCellStyle(highlight, alarm.alarm_number, oldAlarm.alarm_number) },
      { content: alarm.phone, styles: this.getCellStyle(highlight, alarm.phone, oldAlarm.phone) },
      { content: alarm.imei_number, styles: this.getCellStyle(highlight, alarm.imei_number, oldAlarm.imei_number) },
      { content: alarm.type, styles: this.getCellStyle(highlight, alarm.type, oldAlarm.type) },
      { content: alarm.inventory_number, styles: this.getCellStyle(highlight, alarm.inventory_number, oldAlarm.inventory_number) },
    ];

    // SEC-337: Securitas Sverige AB needs the SIM card's IMSI to identify the
    // device on their side, so for that centre one more column is added here,
    // next to IMEI where the device's other identifiers already are. The column
    // is always printed for that centre - blank when nothing was filled in.
    // Both sides of the highlight check fall back to "" so a device that never
    // had the field does not light up as a change.
    if (showImsiNumber) {
      const imsi = alarm.imsi_number || "";
      const oldImsi = oldAlarm.imsi_number || "";
      alarmUnitCol.splice(3, 0, t("alarm_pdf.imsi"));
      alarmUnitBody.splice(3, 0, { content: imsi, styles: this.getCellStyle(highlight, imsi, oldImsi) });
    }

    doc.autoTable({
      startY: y + 27,
      headStyles: { fillColor: [62, 178, 222] },
      head: [alarmUnitCol],
      body: [alarmUnitBody],
    });

    // Changes summary table - show old → new values for IMEI and phone when changed
    if (highlight) {
      let changesBody = [];
      if (alarm.imei_number != oldAlarm.imei_number) {
        changesBody.push([t("alarm_pdf.imei"), oldAlarm.imei_number || "", alarm.imei_number || ""]);
      }
      if (alarm.phone != oldAlarm.phone) {
        changesBody.push([t("alarm_pdf.telematics_number"), oldAlarm.phone || "", alarm.phone || ""]);
      }
      if (changesBody.length > 0) {
        doc.addPage();
        y = 10;
        this.generateHeading(doc, y, t("alarm_pdf.changes_summary"), c);
        c++;
        doc.autoTable({
          startY: y + 27,
          headStyles: { fillColor: [255, 152, 0] },
          head: [[t("alarm_pdf.field"), t("alarm_pdf.previous_value"), t("alarm_pdf.new_value")]],
          body: changesBody,
        });
      }
    }

    // contact persons
    y = doc.lastAutoTable.finalY;
    y = doc.lastAutoTable.finalY;
    if (y > 240) {
      doc.addPage();
      y = 10;
    }

    this.generateHeading(doc, y, t("alarm_pdf.contact_person"), c);
    c++;

    let contact_person_desc = t("alarm_pdf.contact_person_details");
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.text(contact_person_desc, 15, y + 27);

    let contact_persons = [];
    // The old documentation may hold fewer contact rows than the new one (an
    // older device, or one whose rows were padded to three on open), so both
    // sides are read defensively: an out-of-range index means "there was nothing
    // here before" - an empty row, not a crash halfway through the PDF and not a
    // change to highlight (undefined != "" would light the whole row up).
    const EMPTY_CONTACT = { name: "", function: "", phone: "", weekday: "", saturday: "", sunday: "" };
    const contactRow = (list, i) => ({ ...EMPTY_CONTACT, ...((Array.isArray(list) && list[i]) || {}) });
    const newContacts = Array.isArray(alarm.contact_persons) ? alarm.contact_persons : [];
    for (let i = 0; i < newContacts.length; i++) {
      let el = contactRow(newContacts, i);
      let old = contactRow(oldAlarm.contact_persons, i);
      contact_persons.push([
        { content: el.name, styles: this.getCellStyle(highlight, el.name, old.name) },
        { content: el.function, styles: this.getCellStyle(highlight, el.function, old.function) },
        { content: el.phone, styles: this.getCellStyle(highlight, el.phone, old.phone) },
        { content: el.weekday, styles: this.getCellStyle(highlight, el.weekday, old.weekday) },
        { content: el.saturday, styles: this.getCellStyle(highlight, el.saturday, old.saturday) },
        { content: el.sunday, styles: this.getCellStyle(highlight, el.sunday, old.sunday) },
      ]);
    }
    doc.autoTable({
      startY: y + 43,
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.name"), t("alarm_pdf.function"), t("alarm_pdf.number"), t("alarm_pdf.everyday"), t("alarm_pdf.saturday"), t("alarm_pdf.sun_weekend")]],
      body: contact_persons,
    });

    // external contractor
    y = doc.lastAutoTable.finalY;
    this.generateHeading(doc, y, t("alarm_pdf.external_agent"), c);
    c++;

    doc.autoTable({
      startY: y + 27,
      headStyles: { fillColor: [62, 178, 222] },
      head: [[t("alarm_pdf.name"), t("alarm_pdf.contact_number"), t("alarm_pdf.id_perpetrator")]],
      body: [
        [
          { content: alarm.external_contractor.name, styles: this.getCellStyle(highlight, alarm.external_contractor.name, oldAlarm.external_contractor.name) },
          { content: alarm.external_contractor.phone, styles: this.getCellStyle(highlight, alarm.external_contractor.phone, oldAlarm.external_contractor.phone) },
          { content: alarm.external_contractor.object_id, styles: this.getCellStyle(highlight, alarm.external_contractor.object_id, oldAlarm.external_contractor.object_id) },
        ],
      ],
    });

    // password
    y = doc.lastAutoTable.finalY;
    this.generateHeading(doc, y, t("alarm_pdf.authorization_code"), c);
    c++;

    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    if (highlight && alarm.alarm_password != oldAlarm.alarm_password) doc.setTextColor("orange");
    doc.text(alarm.alarm_password, 17, y + 30);
    doc.setTextColor(0, 0, 0);

    // personal password
    y = y + 33;
    this.generateHeading(doc, y, t("alarm_pdf.personal_password"), c);
    c++;

    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    if (highlight && alarm.personal_password != oldAlarm.personal_password) doc.setTextColor("orange");
    doc.text(alarm.personal_password, 17, y + 30);
    doc.setTextColor(0, 0, 0);

    // alarm profile
    y = y + 33;
    if (y > 210) {
      doc.addPage();
      y = 10;
    }

    this.generateHeading(doc, y, t("alarm_pdf.select_alarm_profile"), c);
    c++;

    if (highlight && normalizeRiskProfile(alarm.risk_profile) != normalizeRiskProfile(oldAlarm.risk_profile)) doc.setTextColor("orange");
    doc.text(riskProfileLabel(t, alarm.risk_profile), 17, y + 30);
    doc.setTextColor(0, 0, 0);

    // same texts as the preview in the device forms (AlarmProfilePreview)
    const { title: risk_title, desc: risk_desc } = riskProfileTexts(t, alarm.risk_profile);

    // Customer-specific instruction (set on the business under "Ändra"):
    // printed instead of the standard text for the risk profile.
    const customInstruction = customInstructionText(unit);

    let lastY;
    if (customInstruction) {
      // red heading so the operator sees that this deviates from the standard
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.setTextColor(220, 38, 38);
      doc.text(t("alarm_pdf.custom_instruction"), 17, y + 38);
      doc.setTextColor(0, 0, 0);

      doc.setFontSize(9);
      doc.setFont(undefined, "normal");

      // Free text of any length: print line by line and break pages as needed
      const customLines = doc.splitTextToSize(customInstruction, 177);
      let lineY = y + 45;
      customLines.forEach((line) => {
        if (lineY > 280) {
          doc.addPage();
          lineY = 15;
        }
        doc.text(line, 18, lineY);
        lineY += 5;
      });
      lastY = lineY;
    } else {
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(t("alarm_pdf.primary_input"), 17, y + 38);

      doc.setFontSize(9);
      doc.setFont(undefined, "normal");

      // Always show risk profile content
      doc.text(risk_title, 17, y + 45);

      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(t("alarm_pdf.action_in_alarm"), 17, y + 62);

      doc.setFontSize(9);
      doc.setFont(undefined, "normal");

      // Always show risk description - split into lines to get proper height
      let riskDescLines = doc.splitTextToSize(risk_desc, 177);
      doc.text(riskDescLines, 18, y + 70);

      // Calculate Y position after risk description
      lastY = y + 70 + (riskDescLines.length * 5);
    }
    
    // Guard dispatch ("Larmutryckning av väktare") is a Norwegian-only section:
    // Swedish alarm centrals must never receive it, whichever caller generated
    // the plan. Gating it here rather than at each call site keeps it out of
    // every path — download, single send, bulk send and order device creation —
    // including the order panel, which derives the flag from the external
    // contractor alone and has no alarm-central country to check.
    if (showDirectDispatch && isNorwegian) {
      // Check if we need a new page for direct dispatch section
      if (lastY + 30 > 270) {
        doc.addPage();
        lastY = 10;
      }
      
      // Add Direct Dispatch section using autoTable for consistent yellow highlight
      let dispatchValue = alarm.direct_dispatch
        ? (t("alarm_pdf.direct_dispatch_enabled") || "JA")
        : (t("alarm_pdf.direct_dispatch_disabled") || "NEJ");

      doc.autoTable({
        startY: lastY + 7,
        headStyles: { fillColor: [62, 178, 222] },
        head: [[t("alarm_pdf.direct_dispatch")]],
        body: [[{ content: dispatchValue, styles: this.getCellStyle(highlight, alarm.direct_dispatch, oldAlarm.direct_dispatch) }]],
      });
      lastY = doc.lastAutoTable.finalY;

      // Add the informational text about guard dispatch rules
      let infoText = t("alarm_pdf.direct_dispatch_info") || "";
      if (infoText) {
        let infoLines = doc.splitTextToSize(infoText, 175);
        doc.text(infoLines, 17, lastY + 7);
        lastY = lastY + 7 + (infoLines.length * 5);
      }
    }

    return doc;
  },

  generateHeading(doc, y, title, c) {
    doc.setDrawColor(0, 0, 0);
    doc.setTextColor(0, 0, 0);

    doc.line(15, y + 10, 195, y + 10);

    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text(`${c}. ${title}`, 17, y + 17);

    doc.line(15, y + 21, 195, y + 21);
  },

  generateAgreementPDF(agreement) {
    var doc = new jsPDF();
    
    // Check if agreement is Norwegian
  const isNorwegian = (agreement.country || "").toLowerCase() === "no";
  const currency = isNorwegian ? "NOK" : "SEK";

    // padding common
    let titlePadding = { top: 2, right: 2, bottom: 1, left: 2 };
    let contentPadding = { top: 0, right: 2, bottom: 2, left: 2 };
    let yZeroPadding = { top: 0, right: 2, bottom: 0, left: 2 };

    // logo
    var logo = new Image();
    logo.src = require("@/assets/images/securdia_pdf_logo.png");
    doc.addImage(logo, "PNG", 15, 5, 39.86, 11.29);

    // agreement number
    let agreement_head = isNorwegian 
      ? ["Kontraktsnummer", "E-faktura"]
      : [this.$t("alarm_pdf.agreement_number"), this.$t("alarm_pdf.e_invoice")];
    let agreement_text = [agreement.agreement_number, agreement.e_bill];
    if (agreement.e_bill == "") {
      agreement_head = isNorwegian 
        ? ["Kontraktsnummer"]
        : [this.$t("alarm_pdf.agreement_number")];
      agreement_text = [agreement.agreement_number];
    }

    doc.autoTable({
      startY: 25,
      head: [agreement_head],
      body: [agreement_text],
      theme: "plain",
      tableLineColor: [0, 0, 0],
      tableLineWidth: 0.1,
      margin: { right: 120 },
      headStyles: {
        halign: "right",
        fontSize: 8,
        cellPadding: titlePadding,
      },
      columnStyles: {
        0: { halign: "right", fontSize: 7, cellPadding: contentPadding },
        1: { halign: "right", fontSize: 7, cellPadding: contentPadding },
      },
      didDrawCell: function(data) {
        if (data.column.index === 0) {
          data.doc.setDrawColor(0, 0, 0);
          data.doc.line(data.cell.x + data.cell.width, data.cell.y, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
        }
      },
    });

    // agreement seller, tenants
    doc.autoTable({
      startY: doc.autoTable.previous.finalY + 5,
      showHead: "never",
      body: [[
        { content: isNorwegian ? "Kunde" : this.$t("alarm_pdf.lessee"), styles: { fontSize: 10, fontStyle: "bold", cellPadding: { top: 0, right: 0, bottom: 0, left: 0 } } },
        { content: isNorwegian ? "Leverandør" : this.$t("alarm_pdf.lessor"), styles: { fontSize: 10, fontStyle: "bold", cellPadding: { top: 0, right: 0, bottom: 0, left: 72 } } }
      ]],
      theme: "plain",
    });

    let address_arr = [
      [
        { content: isNorwegian ? "Navn på Kunden" : this.$t("alarm_pdf.name"), colSpan: 2, styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
        { content: isNorwegian ? "Navn på Leverandøren" : this.$t("alarm_pdf.name"), colSpan: 2, styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
      ],
      [
        { content: agreement.customer_name, colSpan: 2, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
        { content: agreement.dealer_name, colSpan: 2, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
      ],
      [
  { content: isNorwegian ? "Organisasjonsnummer" : "Organisationsnummer", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
  { content: isNorwegian ? "Telefon" : "Telefon, äv riktnr", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
  { content: isNorwegian ? "Organisasjonsnummer" : "Organisationsnummer", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
  { content: isNorwegian ? "Telefon" : "Telefon, äv riktnr", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
      ],
      [
        { content: agreement.company_number, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
        { content: agreement.customer_phone, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
        { content: agreement.dealer_company_number, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
        { content: agreement.dealer_phone, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
      ],
      [
        { content: isNorwegian ? "Fakturaadresse" : "Fakturaadress", colSpan: 2, styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
        { content: isNorwegian ? "Adresse" : "Adress", colSpan: 2, styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
      ],
      [
        { content: agreement.billing_adress, colSpan: 2, styles: { fontSize: 7, halign: "left", cellPadding: yZeroPadding } },
        { content: agreement.dealer_address, colSpan: 2, styles: { fontSize: 7, halign: "left", cellPadding: yZeroPadding } },
      ],
      [
        { content: `${agreement.billing_zip_code}, ${agreement.billing_city}`, colSpan: 2, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
        { content: `${agreement.dealer_zip_code}, ${agreement.dealer_city}`, colSpan: 2, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
      ],
      [
  { content: "Kontaktperson", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
  { content: "Ref", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
  { content: isNorwegian ? "Kontaktperson" : "Säljare", colSpan: 2, styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
      ],
      [
        { content: agreement.customer_contact, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
        { content: agreement.customer_reference, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
        { content: this.getSellerName(agreement.seller), colSpan: 2, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
      ],
    ];

    doc.autoTable({
      startY: doc.autoTable.previous.finalY + 1,
      showHead: "never",
      theme: "plain",
      tableLineColor: [0, 0, 0],
      tableLineWidth: 0.1,
      body: address_arr,
      didDrawCell: function(data) {
        if ((data.cell.colSpan === 2 && data.column.index === 0) || data.cell.colSpan === 1) {
          data.doc.setDrawColor(0, 0, 0);
          data.doc.line(data.cell.x + data.cell.width, data.cell.y, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
        }

        if (data.row.index == 1 || data.row.index == 3 || data.row.index == 6) {
          data.doc.setDrawColor(0, 0, 0);
          data.doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
        }
      },
    });

    // unit specifications
    doc.autoTable({
      startY: doc.autoTable.previous.finalY + 3,
      theme: "plain",
  head: [[isNorwegian ? "Utstyr (objektspesifikasjon, produksjonsår, ekstrautstyr etc.)" : "Objektspecifikation, tillverkningsår, extra utrustning mm"]],
      tableLineColor: [0, 0, 0],
      tableLineWidth: 0.1,
      headStyles: {
        halign: "left",
        fontSize: 8,
        cellPadding: titlePadding,
      },
      body: [[{ content: agreement.units_specification, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } }]],
    });

    // contract amount
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text(14, doc.autoTable.previous.finalY + 7, isNorwegian ? "Utstyr" : "Hyresobjekt");

    doc.setFontSize(6);
    doc.setFont(undefined, "bold");
    doc.text(14, doc.autoTable.previous.finalY + 11, isNorwegian ? "Betingelser " : "Villkor ");
    doc.setFont(undefined, "normal");
    doc.text(
      doc.getTextWidth(isNorwegian ? "Betingelser " : "Villkor ") + 15,
      doc.autoTable.previous.finalY + 11, 
      isNorwegian ? "(alle beløp er ekskl. mva.)" : "(samtliga belopp är angivna exklusive mervärdesskatt)"
    );

  // Check agreement type for pricing display
  // cash_services: Cash for hardware + optional monthly services (software, etc.)
  const isCashServicesAgreement = agreement.agreement_category === 'cash_services';
  
  const monthlyCost = parseFloat(agreement.rental_month_cost) || 0;
  
  // Norwegian only: Determine period multiplier and label based on payment_terms
  // Månedlig (1), Kvartalsvis (3), Halvårlig (6), Årlig (12)
  let periodMultiplier = 12; // default to yearly
  let periodLabelNO = "år";
  
  // The dropdown's freetext marker is never printed: the typed text (when the form
  // still carries it separately) or nothing
  const paymentTerms = isPaymentTermsFreetextMarker(agreement.payment_terms)
    ? (agreement.payment_terms_freetext || "").trim()
    : agreement.payment_terms || "";
  
  // Standard payment terms options (Norwegian and Swedish)
  const standardOptions = ["Månedlig", "Kvartalsvis", "Halvårlig", "Årlig", "Månad", "Kvartal", "Halv år", "Årsvis"];
  const isFreetext = paymentTerms && !standardOptions.includes(paymentTerms);
  
  // Use payment_terms directly for display (freetext values are stored directly)
  const displayPaymentTerms = paymentTerms;
  
  if (paymentTerms === "Månedlig") {
    periodMultiplier = 1;
    periodLabelNO = "måned";
  } else if (paymentTerms === "Kvartalsvis") {
    periodMultiplier = 3;
    periodLabelNO = "kvartal";
  } else if (paymentTerms === "Halvårlig") {
    periodMultiplier = 6;
    periodLabelNO = "halvår";
  } else if (paymentTerms === "Årlig") {
    periodMultiplier = 12;
    periodLabelNO = "år";
  }
  
  // Calculate period cost for Norwegian (no decimals)
  const periodCost = monthlyCost ? Math.round(monthlyCost * periodMultiplier) : "";
  
  // Check if we should hide the period price column (when monthly or freetext, the period price is not applicable)
  const isMonthly = paymentTerms === "Månedlig";
  const hidePeriodPrice = isMonthly || isFreetext;
  
  // Format amounts with thousands separator
  const formattedSetupCost = formatAmountWithSeparator(agreement.setup_cost);
  const formattedMonthlyCost = formatAmountWithSeparator(agreement.rental_month_cost);
  const formattedCashSum = formatAmountWithSeparator(agreement.cash_sum);
  const formattedPeriodCost = periodCost ? formatAmountWithSeparator(periodCost) : "";

  // Currency-formatted display values: NOK prefix for Norwegian, SEK suffix for Swedish
  const displaySetupCost = formattedSetupCost ? (isNorwegian ? `NOK ${formattedSetupCost}` : `${formattedSetupCost} ${currency}`) : '';
  const displayMonthlyCost = formattedMonthlyCost ? (isNorwegian ? `NOK ${formattedMonthlyCost}` : `${formattedMonthlyCost} ${currency}`) : '';
  const displayCashSum = formattedCashSum ? (isNorwegian ? `NOK ${formattedCashSum}` : `${formattedCashSum} ${currency}`) : '';
  const displayPeriodCost = formattedPeriodCost ? (isNorwegian ? `NOK ${formattedPeriodCost}` : `${formattedPeriodCost} ${currency}`) : '';

    let amount_arr = [
      [
        { content: isNorwegian ? "Kontraktens startdato" : "Avtalsstart", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
        { content: isNorwegian ? "Kontraktsperiode" : "Avtalsperiod", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
        { content: isNorwegian ? "Betalingsbetingelser" : "Betalningsvillkor", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
      ],
      [
        { content: this.moment(agreement.contract_start).format("YYYY-MM-DD"), styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
        { content: isNorwegian ? `${agreement.contract_period} måneder` : `${agreement.contract_period} mån`, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
        { content: displayPaymentTerms, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
      ],
    ];

    // Add the second row with pricing - show period price based on payment_terms
    // Cash: Only hardware purchase (cash_sum), no monthly costs
    // Cash_services: Hardware purchase (cash_sum) + optional monthly services (rental_month_cost)
    // Rental/Financed: Monthly rental costs with optional period pricing
    
    if (isCashServicesAgreement) {
      // Cash services - show cash sum for hardware + monthly cost for services
      const hasMonthlyCost = monthlyCost > 0;
      
      if (isNorwegian) {
        if (hasMonthlyCost) {
          amount_arr.push([
            { content: "Etableringsgebyr", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
            { content: "Utstyr (Engangskostnad)", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
            { content: "Tjenester pr. måned", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
          ]);
          amount_arr.push([
            { content: displaySetupCost, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
            { content: displayCashSum, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
            { content: displayMonthlyCost, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
          ]);
        } else {
          // No monthly services, only cash hardware
          amount_arr.push([
            { content: "Etableringsgebyr", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
            { content: "Utstyr (Engangskostnad)", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
          ]);
          amount_arr.push([
            { content: displaySetupCost, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
            { content: displayCashSum, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
          ]);
        }
      } else {
        if (hasMonthlyCost) {
          amount_arr.push([
            { content: "Uppläggningsavgift", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
            { content: "Utrustning (kontant)", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
            { content: "Tjänster/mån", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
          ]);
          amount_arr.push([
            { content: `${formattedSetupCost} ${currency}`, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
            { content: `${formattedCashSum} ${currency}`, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
            { content: `${formattedMonthlyCost} ${currency}`, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
          ]);
        } else {
          // No monthly services, only cash hardware
          amount_arr.push([
            { content: "Uppläggningsavgift", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
            { content: "Utrustning (kontant)", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
          ]);
          amount_arr.push([
            { content: `${formattedSetupCost} ${currency}`, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
            { content: `${formattedCashSum} ${currency}`, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
          ]);
        }
      }
    } else {
      // Rental/Financed agreement - show monthly rental cost
      if (isNorwegian) {
        if (hidePeriodPrice) {
          // Hide period price for monthly or freetext payment terms
          amount_arr.push([
            { content: "Etableringsgebyr", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
            { content: "Pris pr. måned", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
          ]);
          amount_arr.push([
            { content: displaySetupCost, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
            { content: displayMonthlyCost, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
          ]);
        } else {
          amount_arr.push([
            { content: "Etableringsgebyr", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
            { content: "Pris pr. måned", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
            { content: `Pris pr. ${periodLabelNO.toLowerCase()}`, styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
          ]);
          amount_arr.push([
            { content: displaySetupCost, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
            { content: displayMonthlyCost, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
            { content: displayPeriodCost, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
          ]);
        }
      } else {
        amount_arr.push([
          { content: "Uppläggningsavgift", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
          { content: "Hyreskostnad/mån", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } },
        ]);
        amount_arr.push([
          { content: `${formattedSetupCost} ${currency}`, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
          { content: `${formattedMonthlyCost} ${currency}`, styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } },
        ]);
      }
    }

    // Insurance: nothing is printed for an insured agreement; one without insurance
    // says so in the same box ("Försäkring: Nej"). An agreement with no insurance
    // recorded at all (older agreements) prints nothing either - not recorded is not "no".
    if (agreement.insurance === false) {
      const insuranceTitle = { content: isNorwegian ? "Forsikring" : "Försäkring", styles: { fontSize: 8, fontStyle: "bold", halign: "left", cellPadding: titlePadding } };
      const insuranceValue = { content: isNorwegian ? "Nei" : "Nej", styles: { fontSize: 7, halign: "left", cellPadding: contentPadding } };
      const priceTitles = amount_arr[amount_arr.length - 2];
      const priceValues = amount_arr[amount_arr.length - 1];
      // next to the prices when the price row has a free column, otherwise on its own row
      if (priceTitles.length < 3) {
        priceTitles.push(insuranceTitle);
        priceValues.push(insuranceValue);
      } else {
        amount_arr.push([insuranceTitle], [insuranceValue]);
      }
    }

    doc.autoTable({
      startY: doc.autoTable.previous.finalY + 13,
      showHead: "never",
      theme: "plain",
      tableLineColor: [0, 0, 0],
      tableLineWidth: 0.1,
      body: amount_arr,
    });

    // special terms
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
  doc.text(14, doc.autoTable.previous.finalY + 7, isNorwegian ? "Spesielle betingelser" : "Särskilda villkor");

    doc.autoTable({
      startY: doc.autoTable.previous.finalY + 9,
      showHead: "never",
      theme: "plain",
      tableLineColor: [0, 0, 0],
      tableLineWidth: 0.1,
      body: [[{ content: agreement.special_terms, styles: { fontSize: 7, fontStyle: "normal", halign: "left", cellPadding: 2 } }]],
    });

    doc.setFontSize(7);
    doc.setFont(undefined, "normal");
    let contractText = isNorwegian 
      ? 'Denne avtalen er utferdiget i to likelydende eksemplarer, hvorav partene beholder hvert sitt. Avtalen er bindende for Kunden ved dennes underskrift på kontrakten og for Securdia AS ved sistnevntes underskrift. Kunden bekrefter herved å ha lest og akseptert samtlige vilkår og betingelser som fremgår på denne siden og i "GENERELLE VILKÅR OG BETINGELSER FOR BRUK AV UTSTYR" som er vedlagt denne avtalen. Vi gjør oppmerksom på at vilkårene blant annet innebærer at Leverandøren eier Utstyret og at Kunden i Kontraktsperioden bærer risikoen for Utstyret.'
      : 'Detta avtal har upprättats i två likalydande exemplar, av vilka parterna tagit var sitt. Avtalet är bindande för Hyrestagaren genom dennes underskrift på kontraktet och för Securdia AB när avtalet undertecknats av denna. Hyrestagaren bekräftar härmed att de tagit del av och godkänt samtliga villkor enligt denna sida samt enligt "ALLMÄNNA VILLKOR LEASING" vilka bifogas detta avtal. Observera att hyra av lös egendom bland annat innebär att Hyrestagaren väljer Hyresobjekt och Leverantör samt står riskerna för inköp, funktion och användning.';
    let splitText = doc.splitTextToSize(contractText, 180);
    doc.text(14, doc.autoTable.previous.finalY + 5, splitText);

    // signature
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
  doc.text(14, doc.autoTable.previous.finalY + 21, isNorwegian ? "Underskrifter - Underskriftsberettiget" : "Underskrifter - Firmateckning");

    let sign_arr = [
      [
        { content: isNorwegian ? "Sted og dato" : "Ort och datum", styles: { fontSize: 8, fontStyle: "bold", halign: "left" } },
        { content: isNorwegian ? "Sted og dato" : "Ort och datum", styles: { fontSize: 8, fontStyle: "bold", halign: "left" } },
      ],
      [
        { content: isNorwegian ? "Signatur Leverandør Securdia AS" : "Underskrift Hyresgivare Securdia AB", styles: { fontSize: 8, fontStyle: "bold", halign: "left" } },
        { content: isNorwegian ? "Signatur Kunden" : "Underskrift Hyrestagare", styles: { fontSize: 8, fontStyle: "bold", halign: "left" } },
      ],
      [
        { content: isNorwegian ? "Navn og stilling" : "Namnförtydligande", styles: { fontSize: 8, fontStyle: "bold", halign: "left" } },
        { content: isNorwegian ? "Navn og stilling" : "Namnförtydligande och befattning", styles: { fontSize: 8, fontStyle: "bold", halign: "left" } },
      ],
    ];

    doc.autoTable({
      startY: doc.autoTable.previous.finalY + 25,
      theme: "grid",
      showHead: "never",
      styles: {
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
        cellPadding: { top: 2, right: 2, bottom: 6, left: 2 },
      },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 90 }
      },
      body: sign_arr,
    });

    // footer
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(0, 51, 102);
    if (isNorwegian) {
      doc.text("Securdia AS, Hovedkontor Oslo, org. Nr. (936 332 897) Hvamstubben 17, 2013 Skjetten", 100, 289, null, null, "center");
  doc.text("Okonomi@securdia.no\tTel: +47 22 55  50 01", 100, 292, null, null, "center");
    } else {
      doc.text("Securdia AB, Huvudkontor Stockholm, org. Nr. 559270-2087 Ekbacksvägen 22, 3tr 168 69 Bromma", 100, 289, null, null, "center");
      doc.text("Ekonomi@securdia.se	Tel: +46 8-520 276 42", 100, 292, null, null, "center");
    }

    // back
    doc.addPage();

    let content_fsize = 5.5;
    let text_padding = { top: 1, right: 1, bottom: 1, left: 1 };

    const swedishTerms = [
      [
        {
          content: "ALLMÄNNA VILLKOR LEASING",
          styles: { fontSize: content_fsize, fontStyle: "bold", halign: "left", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "För leasingavtal mellan å ena sidan Securdia AB (”SC”) och å andra sidan leasetagaren (”LT”), gemensamt ”Parterna” gäller följande allmänna villkor om inte annat skriftligen avtalats mellan parterna.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "1. Mottagande, undersökning, reklamation och leveransgodkännande LT ska på egen bekostnad vidtaga erforderliga åtgärder för att kunna mottaga leaseobjektet när det av SC ställs till LT:s förfogande. Risken för leaseobjektet övergår till  LT när leaseobjektet överlämnas till LT eller, om leaseobjektet ska transporteras till LT, när leaseobjektet överlämnas till den transportör som har åtagit sig transporten från SC. När risken har övergått till LT, är LT skyldig att betala för leaseobjektet även om det förstörs, kommer bort, försämras eller minskar i värde genom en händelse som inte beror på SC. LT ska vid leverans av leaseobjektet på egen bekostnad omedelbart undersöka detta. Undersökningen ska utföras noggrant och enligt normal branschpraxis. Som bekräftelse på att leaseobjektet erhållits och godkänts, ska LT utan dröjsmål underteckna och tillställa SC detta avtal. Om leaseobjektet är felaktigt ska LT genast skriftligen reklamera till SC. LT ska även anses ha godkänt leveransen av objektet, med samma verkan som genom att tillställa SC ett påskrivet avtal, genom att ta objektet i bruk eller genom att erlägga leasingkostnad eller annan betalning i enlighet med detta avtal, jämte om LT inte senast 10 dagar efter mottagen leverans av leaseobjektet skriftligen reklamerar till SC.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "2. Fel, brist, dröjsmål m.m. LT ska utan dröjsmål underrätta SC om fel eller skada på, samt stöld av, leaseobjektet under leasetiden. SC ska efter sådan underrättelse meddela LT vilka åtgärder LT ska vidta. Vid garanti- eller försäkringsfall upprättar SC skadeanmälan till försäkringsbolaget. Vid behov ska leaseobjektet besiktas gemensamt av SC och LT, för att säkerställa leaseobjektets goda skick. Samtliga skador på leaseobjektet som inte förelåg vid leveranstillfället ska ersättas av LT. Skadat eller förlorat leaseobjekt som inte ersätts genom leaseobjektets försäkring debiteras LT till nyanskaffningsvärde.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "3. Försenad eller felaktig leverans Om SC inte tillhandahåller leaseobjektet i funktionsdugligt skick vid avtalad tidpunkt har LT rätt att häva leasingavtalet eller begära sådan nedsättning av leasingkostnaden som svarar mot felet eller dröjsmålet. SC är dock inte skyldig att ersätta LT för annan skada (direkt skada, indirekt skada eller följdskada) som LT eller tredje man åsamkats till följd av felet eller dröjsmålet. Hävning, skadestånd eller nedsättning av leasingkostnaden får inte göras gällande, när SC erbjuder sig att avhjälpa fel på leaseobjektet eller ersätta leaseobjektet med annan godtagbar ersättningsprodukt, och rättelse sker inom skälig tid och utan kostnad för LT. Sådan påföljd får inte heller göras gällande om inte LT genast efter det att LT märkt, eller bort märka, felet meddelar SC härom (reklamation). Har LT inte undersökt leaseobjektet vid leverans, får LT inte såsom fel åberopa vad LT borde ha märkt vid undersökningen, om inte SC har handlat i strid mot tro och heder.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "4. Start av Leasetid, Leveransperiod m.m. Leasetiden börjar den första dagen i nästkommande kalendermånad efter leverans (enligt punkten Mottagande ovan). Med kalendermånad avses månader med början 1:a januari, 1:a februari, 1:a mars och 1:a april osv. Från datum för fullgjord leverans till leasetidens första dag debiteras LT en avgift om en trehundrasextiodels (1/360) leasekostnad per dag. Avgiften debiteras på LT:s första faktura och förfaller till betalning samtidigt med leasingkostnaden. Eventuell sådan avgift jämte leasingkostnad för tolv månader (”Leasingkostnaden”), ska erläggas och vara SC tillhanda senast trettio dagar efter leasetidens första dag. Leasingkostnaden erlägges därefter, om ej annat skriftligen överenskommits, årsvis i förskott och ska vara SC tillhanda senast sista bankdagen  före varje betalningsperiods början.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "5. Övriga kostnader SC:s samtliga kostnader, såsom kostnader för transport, installation, försäkring, tull, skatt, kursdifferenser och dylikt som avser leaseobjektet och som inte inkluderats i leasingkostnaden, ska vid anfordran betalas av LT.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "6. Indexering och allmänna betalningsvillkor Om arbetskostnadsindex förändras äger SC rätt att justera framtida avgifter proportionellt med förändringen av detta index. Tillämpning sker enligt SCB:s senast publicerade AKI, B–S exkl. O, på årsvärdesnivå (för närvarande maj månad varje år). Samtliga priser gäller exklusive mervärdeskatt. LT betalar leasingkostnaden i förskott. Om inte annat överenskommits är betalningstiden 30 dagar netto. Dröjsmålsränta debiteras med riksbankens fastställda, vid varje tid gällande referensränta med tillägg av åtta procentenheter, vartill – i förekommande fall – kommer lagstadgad påminnelse- och inkassoavgift. Fakturering sker en gång per avtalsår, avslutade leasingavtal faktureras snarast. SC förbehåller sig rätten att debitera faktureringsavgift samt uppläggningsavgift för nya konton.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "7. Mervärdesskatt, dröjsmålsränta, kravavgifter m.m. Utöver leasingkostnad ska LT erlägga mervärdesskatt samt andra skatter och avgifter som sammanhänger med leaseobjektet eller leaseförhållandet. Mervärdesskatt erlägges samtidigt med leasingkostnaden, övriga skatter och avgifter vid anfordran. Utöver leasingkostnad utgår särskilda avgifter som vid var tid tillämpas av SC, såsom för bevakning och indrivning av fordran hos LT, häri särskilt inbegripet inkassoavgifter och avgifter för betalningspåminnelser samt för kostnader i samband med återtagande av leaseobjektet. Vid dröjsmål med erläggande av leasingkostnad eller annan betalning enligt detta avtal, ska LT erlägga dröjsmålsränta på förfallet belopp med för närvarande 2% per månad.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "8. Förfogande över leaseobjektet och användning m.m. Leaseobjektet är SC:s egendom och LT förvärvar ej på grund av leasingavtalet någon äganderätt till objektet. SC upplåter leaseobjektet till LT på de villkor som anges i detta avtal. LT äger inte rätt att vidta åtgärd som äventyrar SC:s äganderätt till leaseobjektet. LT får till exempel inte: a) införliva leaseobjektet med sin eller annans fasta eller lösa egendom; b) vidtaga ändringar i eller påbyggnader av leaseobjektet utan medgivande från SC; c) pantsätta eller överlåta leaseobjektet; d) överlåta detta avtal på annan, uthyra leaseobjektet till annan eller genom avtal med annan förfoga över rätt till leaseobjektet eller detta avtal; e) förflytta leaseobjektet från den angivna adressen enligt detta avtal. Skulle under leasetiden rättslig åtgärd, såsom utmätning, kvarstad, e.d. vidtagas beträffande leaseobjektet, är LT skyldig att för utmätningsmannen uppvisa sitt exemplar av leasingavtalet samt att upplysa utmätningsmannen om SC:s rätt till leaseobjektet. LT är dessutom skyldig att omedelbart skriftligen underrätta SC om den av utmätningsmannen vidtagna åtgärden. LT ansvarar för att leaseobjektet endast brukas av därtill behörig och utbildad personal.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "9. Besiktning SC äger under leasetiden rätt att besiktiga leaseobjektet. LT ska lämna SC tillträde till den lokal där leaseobjektet finns samt lämna erforderligt biträde vid genomförandet av sådan besiktning.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "10. Vård av leaseobjektet och service LT ska väl vårda och underhålla leaseobjektet, inklusive att följa användarinstruktioner, så att leaseobjektet är i gott funktionellt skick. Vid leasetidens upphörande ska leaseobjektet ej vara i sämre skick än vad som är normalt med hänsyn till ålder och normal användning. Skulle leaseobjektet på grund av förslitning, vanvård eller annat skäl bli obrukbart före leasetidens utgång är LT trots detta skyldig att fullgöra sina förpliktelser enligt detta Avtal. Det åligger LT att ombesörja och bekosta eventuellt erforderlig reparation av leaseobjektet förutom i de fall då fråga är om garanti- eller försäkringsfall. Under tid då leaseobjektet är under reparation ska full leasingkostnad utgå. LT får inte ändra produktmärkning, identifikation eller skriftliga användarinstruktioner avseende leaseobjektet. LT får inte, med undantag för vård av leaseobjektet, utan SC:s skriftliga godkännande låta reparera eller förändra leaseobjektet. Skador och defekter i leaseobjektet som under leasetiden uppstår på grund av LT:s brukande av leaseobjektet eller annars på grund av LT eller tredje man, ska bekostas av LT. Reparationer av fel i leaseobjektet som förelegat vid avtalstidens början bekostas av SC. SC tillhandahåller service på leaseobjektet vilken ingår i leasingkostnaden. Service tillhandahålls normalt på plats hos leasetagaren. SC är inte skyldigt att ställa ersättningsobjekt till LT:s förfogande under reparations- eller servicetid.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "11. Försäkring SC tecknar de försäkringar som SC finner är erforderliga för leaseobjektet. Om ej annat skriftligen överenskommits utgår avgift för sådan försäkring utöver leasingkostnaden men faktureras samtidigt med den. LT överlåter härmed till SC all rätt till ersättning vid försäkringsfall. Samtliga utfallande försäkringsbelopp ska tillfalla SC. SC är därutöver berättigad att av LT erhålla självriskbelopp som avräknas vid reglering av försäkringsfall. Vid försäkringsfall ska LT utan dröjsmål anmäla skada på eller förlust av leaseobjektet, eller del därav, till försäkringsbolaget samt skicka kopia av anmälan till SC. Vid skadeanmälan ska kopia av detta avtal inges till försäkringsbolaget. På LT:s begäran kan skriftlig information om de fullständiga villkoren för tecknad försäkring tillhandahållas.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "12. Skada på leaseobjekt m.m. LT svarar från leverans av leaseobjektet t o m den dag leaseobjektet mottagits av SC eller dennes representant i samband med avtalets upphörande, oberoende av vållande, för förlust av eller skada på leaseobjektet. Således är LT betalningsskyldig och ansvarig för skada, inklusive självrisk, som inte täcks av försäkring. a) Om leaseobjektet helt eller delvis förloras, förslits eller förstörs, ska LT omedelbart underrätta SC därom. Härvid har SC rätt att begära särskild säkerhet av LT för återstående betalningar enligt detta avtal;. b) Skada på eller förlust av leaseobjektet befriar inte LT från dennes förpliktelser enligt detta avtal. Detta gäller även om LT inte kan bruka leaseobjektet; c) Vid skada på leaseobjektet där SC anser att reparation bör ske ska utfallande försäkringsersättning användas till betalning av reparationskostnad. LT svarar för de reparations- och övriga kostnader som inte täcks av försäkringen. Om leaseobjektet, eller del därav, helt förloras eller skadas så att reparation enligt SC:s bedömning ej kan ske, ska LT fortsätta att erlägga leasingkostnaden till SC fram till dess SC erhållit ersättning från försäkringsbolaget, alternativt leasingavtalet upphör. Om skadan och ersättningen enligt ovan avser hela leaseobjektet ska leasingavtalet upphöra att gälla när SC erhållit full betalning enligt denna punkt, om skadan och ersättningen avser del av leaseobjektet ska leasingavtalet gälla oförändrat avseende övriga delar.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "13. Skada orsakad av leaseobjektet Det åligger LT att, oberoende av vållande, svara för all sak-, person-, och/eller förmögenhetsskada som leaseobjektet eller dess användning förorsakar, samt hålla SC skadeslös och ansvarsfri med anledning av eventuella krav på grund av dylika skador.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "14. Myndighets föreskrift m.m. LT förbinder sig att under leasetiden inte bruka leaseobjektet utan att dessförinnan förvissa sig om att detsamma i vad mån avser utrustning, skyddsanordningar och dylikt uppfyller alla gällande författningar och av myndighet uppställda krav. SC är inte ansvarig om hinder i utnyttjandet av leaseobjektet föreligger eller uppstår genom föreskrift i författning och/eller myndighets beslut. Sådant hinder kan ej utgöra grund för ersättningskrav från LT gentemot SC och ej heller åberopas som skäl till befrielse helt eller delvis från förpliktelser som åvilar LT.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "15. Leasetid, förlängning och återlämnande Leasetiden är angiven på avtalets framsida. Om LT inte skriftligen till SC senast 90 dagar före avtalad leasetidens utgång eller, om förlängning skett, före förlängningsperiodens utgång, meddelat SC att återlämnande ska ske, förlängs leasetiden varje gång sådant meddelande uteblir, automatiskt med 12 månader, nedan kallad förlängningsperioden. Leasingkostnaden under förlängningsperioden äger SC rätt att justera till den vid den tidpunkten gällande prisnivå som gäller för motsvarande leaseobjekt. För förlängningsperioden gäller samma betalningsperiodicitet och samma förfallodagar som för den ursprungliga leasetiden. Under förlängningsperioden fortsätter i övrigt villkoren i detta avtal att gälla oförändrade. I annat fall ska LT, senast inom 30 dagar efter leasetidens utgång, på egen bekostnad och risk återlämna leaseobjektet, inklusive samtliga tillbehör såsom laddsladd, bärtillbehör m.m., till av SC anvisad plats i Sverige. Om leaseobjektet vid återlämnandet är i sämre skick än vad som är normalt med hänsyn till ålder och normal användning äger SC att på LT:s bekostnad åtgärda sådana brister i leaseobjektets skick. Om återlämnande av leaseobjektet inkl samtliga tillbehör inte sker i rätt tid, debiteras LT till nyanskaffningsvärde.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "16. SC:s rätt till uppsägning av avtalet, skadestånd SC äger rätt att med omedelbar verkan uppsäga avtalet och genast på LT:s bekostnad återtaga leaseobjektet om: a) LT dröjer med erläggande av leasingkostnad, förfallna skatter, avgifter eller andra kostnader enligt avtalet med mer än 30 dagar; b) LT inställer betalningarna, ansöker om företagsrekonstruktion eller om sådan beslutas beträffande LT, försätts i konkurs, begär eller inleder ackordsförhandling, träder i likvidation eller eljest enligt SC:s bedömning kan befaras vara på obestånd; c) LT vanvårdar leaseobjektet eller förvägrar SC besiktning av leaseobjektet; d) det enligt SC:s uppfattning finns skälig anledning att anta att LT inte kommer att fullgöra sina förpliktelser mot SC enligt leasingavtalet eller annan förpliktelse mot SC; e) LT i övrigt åsidosätter någon bestämmelse i leasingavtalet; f) LT inte svarar på SC:s frågor eller på annat sätt inte bidrar till att SC löpande uppnår kundkännedom. Om uppsägningsgrund föreligger enligt ovan äger SC även rätt att säga upp annat avtal med LT. För den händelse att avtalet sägs upp av SC av anledning som anges ovan, ska SC äga rätt till, förutom leasingkostnader och andra belopp som är förfallna, även skadestånd som, om inte annan skada kan visas, ska uppgå minst till avgifter för resterande leasetid, allt diskonterat till nuvärde vid uppsägningstidpunkten. Nuvärdesdiskontering ska ske till en räntesats motsvarande gällande STIBOR 30 dagar. Därtill kommer samtliga kostnader för återtagande av leaseobjektet. Skadeståndet ska minskas med intäkt, exkl. moms, som SC efter avdrag för försäljnings-, rekonditionerings- och övriga kostnader erhåller vid försäljning av leaseobjektet, om sådan är möjlig enligt SC:s gottfinnande.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "17. Överlåtelse Avtalet och därmed följande rättigheter och skyldigheter får inte, helt eller delvis, överlåtas av part utan att den andra parten har lämnat sitt skriftliga medgivande till sådan överlåtelse.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "18. Ändring och tillägg Ändringar och tillägg till detta avtal ska ske skriftligen och undertecknas av båda parter.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "19. Meddelanden m.m. LT ska omedelbart underrätta SC om adress- och/eller namnändring. Meddelande rörande detta avtal som SC sänder till LT, ska anses ha nått LT senast på fjärde dagen efter avsändandet, om brevet sänts till i avtalet angiven adress eller annan av SC känd adress. Meddelande som sänts med e-post ska anses ha nått LT omedelbart.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "20. Ansvarsbegränsning SC är inte ansvarig gentemot LT för skada som beror på lagbud, myndighetsåtgärd, krigshändelse, strejk, blockad, bojkott, lockout eller annan liknande omständighet utanför SC:s kontroll. Detta förbehåll gäller vare sig SC själv är part i eller föremål för eller vidtar sådan åtgärd. Förhindras SC , till följd av omständighet som anges ovan, att fullgöra åtagande gentemot LT, ska SC vara berättigad att uppskjuta fullgörandet härav till dess hindret upphört. SC är heller inte skyldig att ersätta skada som uppkommer, om SC varit normalt aktsam. SC är inte i något fall skyldig att utge ersättning för indirekt skada eller följdskada (utebliven vinst, produktionsbortfall m.m.).",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "21. Lagval och Forum Svensk lag ska tillämpas på detta avtal. Tvister som uppstår i anledning av avtalet ska slutligt avgöras genom skiljedomsförfarande administrerat av SCC Skiljedomsinstitut (SCC). Regler för Förenklat Skiljeförfarande ska tillämpas om inte SCC med beaktande av målets svårighetsgrad, tvisteföremålets värde och övriga omständigheter bestämmer att Skiljedomsregler ska tillämpas. I sistnämnda fall ska SCC också bestämma om skiljenämnden ska bestå av en eller tre skiljedomare. Skiljeförfarandets säte ska vara Stockholm. Språket för förfarandet ska vara svenska. Skiljeförfarande som påkallats med hänvisning till denna skiljeklausul omfattas av sekretess. Sekretessen omfattar all information som framkommer under förfarandet liksom beslut eller skiljedom som meddelas i anledning av förfarandet.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "22. Information om behandling av personuppgifter För LT:s personuppgifter är SC personuppgiftsansvarig. SC behandlar LT:s personuppgifter i enlighet med vid var tid gällande integritetspolicy som finns tillgänglig på SC:s webbsida. SC redovisar informationen om behandlingen av personuppgifter för LT i samband med att LT ingår avtalet varigenom LT godkänner SC:s personuppgiftsbehandling. LT är införstådd med att SC för att leva upp till de rättsliga krav som ställs beträffande dokumentation för bl.a. förhindrande av penningtvätt, måste behandla LT:s personuppgifter.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: '23. Programvara och licensrättigheter I leaseobjektet ingår programvara/licensrättighet till SecurCloud (nedan "Programvaran"). Genom detta avtal godkänner och bekräftar SC att programvaran får nyttjas av LT i enlighet med villkoren i detta avtal. SC ansvarar inte i något fall för skada med anledning av LT:s nyttjande av programvaran (förlust av data, direkt skada, indirekt skada eller följdskada).',
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
    ];

    const norwegianTerms = [
      [
        {
          content: "GENERELLE VILKÅR OG BETINGELSER FOR BRUK AV UTSTYR",
          styles: { fontSize: content_fsize, fontStyle: "bold", halign: "left", cellPadding: text_padding },
        },
      ],
      [
        {
          content: 'For avtaler om bruk av Utstyret mellom Leverandør, Securdia AS ("SC") på den ene siden og Kunden på den andre siden, i fellesskap "Partene", gjelder følgende generelle vilkår og betingelser, med mindre annet er skriftlig avtalt mellom Partene.',
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: text_padding },
        },
      ],
      [
        {
          content: "1. Mottakelse, undersøkelse, reklamasjon og leveringsgodkjenning Kunden skal for egen regning iverksette nødvendige tiltak for å kunne motta Utstyret når det gjøres tilgjengelig av SC. Risikoen for Utstyret overgår til Kunden når Utstyret overleveres til Kunden eller, dersom Utstyret skal transporteres til Kunden, når Utstyret overleveres til den transportør som har påtatt seg transporten fra SC. Når risikoen har overgått til Kunden, er Kunden forpliktet til å betale for Utstyret selv om det blir ødelagt, bortkommet, forringet eller reduseres i verdi ved en hendelse som ikke skyldes SC. Kunden skal ved levering av Utstyret for egen regning umiddelbart undersøke dette. Undersøkelsen skal utføres nøye og i henhold til normal bransjepraksis. Som bekreftelse på at Utstyret er mottatt og godkjent, skal Kunden uten opphold undertegne og sende dette avtaledokumentet til SC. Dersom Utstyret er feilaktig, skal Kunden straks skriftlig reklamere til SC. Kunden skal også anses å ha godkjent leveransen av Utstyret, med samme virkning som ved å sende SC et signert avtaledokument, ved å ta Utstyret i bruk eller ved å betale Bruksprisen eller annen betaling i henhold til denne avtalen, samt dersom Kunden ikke innen ti dager etter mottatt levering av Utstyret skriftlig reklamerer til SC.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        }
      ],
      [
        {
          content: "2. Feil, mangler, forsinkelser m.v. Kunden skal uten opphold varsle SC skriftlig om eventuelle feil, skader på eller tyveri av Utstyret i Kontraktsperioden. Etter slik varsling skal SC informere Kunden om hvilke tiltak Kunden skal iverksette. Ved garanti- eller forsikringskrav skal SC utarbeide skademelding til forsikringsselskapet og Kunden skal på SCs begjæring bidra ved å gi etterspurt informasjon. Om nødvendig skal Utstyret inspiseres av SC og Kunden i fellesskap for å sikre at Utstyret er i god stand. Alle skader på Utstyret som ikke forelå på leveringstidspunktet, skal erstattes av Kunden. Skadet eller tapt Utstyr som ikke dekkes av Utstyrets forsikring, belastes Kunden til gjenanskaffelsespris.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 1.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "3. Forsinket eller feilaktig levering Dersom SC ikke leverer Utstyret i funksjonsdyktig stand (mangel) til avtalt tid, har Kunden rett til å kreve et slikt avslag i Årsprisen som svarer til feilen eller forsinkelsen. Ved vesentlig forsinket eller feilaktig levering kan Kunden heve avtalen. SC er ikke forpliktet til å erstatte tap ut over dette (hverken direkte eller indirekte tap, herunder konsekvenstap) som Kunden eller tredjemann lider som følge av feilen eller forsinkelsen. Heving, erstatning eller prisavslag kan uansett ikke kreves når SC tilbyr seg å utbedre mangelen ved Utstyret eller erstatte Utstyret med et annet akseptabelt erstatningsprodukt, og utbedring skjer innen rimelig tid og uten kostnad for Kunden. Slike mangelskrav kan under enhver omstendighet ikke gjøres gjeldende med mindre Kunden varsler SC umiddelbart etter at Kunden har eller burde ha oppdaget mangelen (reklamasjon). Dersom Kunden ikke har undersøkt Utstyret ved levering, kan Kunden ikke påberope seg som mangel det Kunden burde ha oppdaget ved undersøkelsen, med mindre SC har handlet i strid med redelighet og god tro.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 1.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "4. Oppstart, levering, Kontraktsperiode m.m. Kontraktsperioden starter den første dagen i kalendermåneden etter levering (i henhold til punkt 1 ovenfor). Med kalendermåned menes måneder som starter 1. januar, 1. februar, 1. mars, 1. april osv. Fra dato for fullført levering og frem til første dag i Kontraktsperioden belastes Kunden med kostnad på én trehundreogsekstidel (1/360) av Årsprisen per dag. Kostnaden belastes Kundens første faktura og skal betales samtidig med Årsprisen. Denne kostnaden, sammen med månedsprisen for tolv måneder (\"Årspris\"), skal betales og være mottatt av SC senest tretti dager etter den første dagen i Kontraktsperioden. Deretter, med mindre annet er skriftlig avtalt, skal Årsprisen betales årlig forskuddsvis og være SC i hende senest den siste bankdagen før starten av hver nye tolvmånedersperiode.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 1.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "5. Andre kostnader Alle kostnader som påløper for SCs tilgjengeliggjøring av Utstyret for Kunden, herunder kostnader for transport, installasjon, forsikring, toll, skatter, avgifter, valutakursdifferanser og lignende knyttet til Utstyret og som ikke er inkludert i Årsprisen, skal betales av Kunden ved påkrav.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 3, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "6. Indeksregulering og generelle betalingsbetingelser Dersom arbeidskostnadsindeksen (AKI) endres, har SC rett til å justere fremtidig vederlag forholdsmessig med endringen i denne indeksen. Regulering skjer i henhold til Statistikmyndigheten SCBs sist publiserte AKI, B–S ekskl. O, på årsverdinivå (for tiden mai hvert år). Alle priser spesifisert på første side er angitt eksklusive merverdiavgift. Kunden skal betale Årsprisen på forskudd, årlig mot faktura. Med mindre annet er avtalt, er betalingsfristen 30 dager. Forsinkelsesrenter beregnes med den til enhver tid gjeldende forsinkelsesrente i samsvar med forsinkelsesrenteloven (lov 1976-12-17 nr. 100) (per i dag 12,25 prosent), eventuelt med tillegg av lovpålagte purre- og inkassogebyrer. Avsluttede avtaler faktureres så snart som mulig. SC forbeholder seg retten til å kreve et faktureringsgebyr og et etableringsgebyr for nye bestillinger.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 1.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "7. MVA I tillegg til Årsprisen skal Kunden betale merverdiavgift og andre skatter og avgifter knyttet til Utstyret eller avtaleforholdet. Merverdiavgift betales samtidig med Årsprisen. Eventuelle andre skatter og avgifter skal betales etter påkrav. I tillegg til Årsprisen skal det betales særskilte gebyrer som SC fra tid til annen anvender, for eksempel for overvåking og innkreving av utestående fordringer mot Kunden, herunder særlig inkassogebyrer og gebyrer for betalingspåminnelser og for kostnader i forbindelse med tilbakeføring av Utstyret. Ved forsinkelse med betaling av Årspris eller annen betaling i henhold til denne avtalen, skal Kunden betale forsinkelsesrenter på det forfalte beløpet i tråd med punkt 6 ovenfor.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 2.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "8. Eiendomsrett Utstyret eies av SC. Denne avtalen gir ikke Kunden noen eiendomsrett til Utstyret, men SC stiller Utstyret til Kundens disposisjon og gir Kunden rett til å bruke Utstyret i Kontraktsperioden på de vilkår som fremgår av denne avtalen. Kunden har ikke rett til å foreta seg noe som setter SCs eiendomsrett til Utstyret i fare. Kunden kan for eksempel ikke: a) innlemme Utstyret i egen eller andres faste eiendom eller løsøre; b) foreta endringer eller tilføyelser i Utstyret uten samtykke fra SC; c) pantsette eller overdra Utstyret; d) overdra denne avtalen til en annen, leie ut Utstyret til en annen eller overdra bruksretten til Utstyret eller denne avtalen; e) flytte Utstyret fra Kundens adresse angitt i denne avtalen. Dersom det i Kontraktsperioden foretas rettslige skritt som beslag, utlegg etc. i Utstyret, plikter Kunden å fremlegge sitt eksemplar av avtalen for namsmannen og opplyse namsmannen om at det er SC som eier Utstyret. Kunden plikter også å underrette SC skriftlig og umiddelbart om hva namsmannen har foretatt seg. Kunden er ansvarlig for at Utstyret kun brukes av autorisert og opplært personell.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 2, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "9. Inspeksjon SC har rett til å inspisere Utstyret i Kontraktsperioden. Kunden skal gi SC adgang til lokalene der Utstyret befinner seg og yte nødvendig bistand ved gjennomføring av slik inspeksjon.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 2, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "10. Vedlikehold av Utstyret og service Kunden skal ta vare på og vedlikeholde Utstyret på en forsvarlig måte, herunder følge bruksanvisningen, slik at Utstyret er i god funksjonell stand. Ved utløpet av Kontraktsperioden skal Utstyret ikke være i dårligere stand enn det som er normalt for dets alder og normale bruk. Dersom Utstyret blir ubrukbart før utløpet av Kontraktsperioden på grunn av slitasje, forsømmelse eller andre årsaker, er Kunden likevel forpliktet til å oppfylle sine forpliktelser etter denne avtalen. Kunden er ansvarlig for å besørge og bekoste eventuelle nødvendige reparasjoner av Utstyret, med unntak av garanti- og forsikringskrav. Årsprisen reduseres ikke for eventuell tid Utstyret er under reparasjon. Kunden skal ikke endre produktmerking, identifikasjon eller skriftlige bruksanvisninger på Utstyret. Bortsett fra vedlikehold av Utstyret, kan Kunden ikke reparere eller endre Utstyret uten skriftlig samtykke fra SC. Kunden har ansvar for skader og mangler på Utstyret som oppstår i Kontraktsperioden som følge av Kundens bruk av Utstyret eller som for øvrig skyldes Kunden eller tredjemann. Utbedring av mangler ved Utstyret som forelå ved levering til Kunden, skal betales av SC. SC yter service på Utstyret som er inkludert i Årsprisen. Servicen utføres normalt på stedet i Kundens lokaler. SC er ikke forpliktet til å stille erstatningsobjekter til rådighet for Kunden i reparasjons- eller serviceperioden.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "11. Forsikring SC tegner de forsikringer som SC anser nødvendige for Utstyret. Med mindre annet er skriftlig avtalt, har SC rett til å få dekket kostnader for slik forsikring fra Kunden i tillegg til Årsprisen. Alle forsikringsbeløp som utbetales skal tilfalle SC. Eventuell forsikringsutbetaling til Kunden skal overdras fra Kunden til SC. SC har videre rett til å motta fra Kunden et overskytende beløp som trekkes fra ved oppgjør av et forsikringskrav. I tilfelle forsikringssak skal Kunden uten opphold varsle forsikringsselskapet om skade på eller tap av Utstyret, eller del av dette, og sende kopi av varselet til SC. Kopi av denne avtalen skal sendes til forsikringsselskapet ved skademelding. På Kundens anmodning kan det gis skriftlig informasjon om de fullstendige vilkår for den forsikring som er tegnet.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 1.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "12. Risiko for Utstyret i Kontraktsperioden Kunden er ansvarlig for tap av eller skade på Utstyret fra dato for levering av Utstyret til dato for tilbakelevering av Utstyret til SC eller dennes representant i forbindelse med opphør av avtalen, uavhengig av skyld. Kunden er således erstatningspliktig og ansvarlig for enhver skade, herunder egenandel, som ikke dekkes av forsikring. a) Dersom Utstyret helt eller delvis går tapt, slites ut eller ødelegges, skal Kunden straks varsle SC. SC har i den forbindelse rett til å kreve særskilt sikkerhetsstillelse fra Kunden for gjenstående betalinger i henhold til denne avtalen. b) Skade på eller tap av Utstyret fritar ikke Kunden fra sine forpliktelser etter denne avtalen. Dette gjelder selv om Kunden ikke kan benytte Utstyret. c) Ved skade på Utstyret hvor SC mener at reparasjon bør foretas, skal utbetalt forsikringserstatning benyttes til å betale reparasjonskostnadene. Kunden er ansvarlig for reparasjon og andre kostnader som ikke dekkes av forsikringen. Dersom Utstyret, eller del av dette, går fullstendig tapt eller skades slik at det etter SCs vurdering ikke kan repareres, skal Kunden fortsette å betale Årspris til SC inntil SC har mottatt erstatning fra forsikringsselskapet, eller til avtalen opphører. Dersom skaden og erstatningen som nevnt ovenfor gjelder hele Utstyret, opphører avtalen å gjelde når SC har mottatt full betaling i henhold til dette punktet. Dersom skaden og erstatningen gjelder en del av Utstyret, gjelder avtalen uendret for de øvrige delene.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 1.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "13. Skader forårsaket av Utstyret Kunden skal være ansvarlig, uavhengig av skyld, for enhver skade på eiendom, person og/eller materiell forårsaket av Utstyret eller bruken av det, og skal holde SC skadesløs for ethvert krav som oppstår som følge av slik skade. Unntak foreligger dersom skaden skyldes grov uaktsomhet eller forsett fra SC eller dersom det oppstår personskade som følge av produktansvar som omfattes av produktansvarsloven §§ 2-3 og som Leverandør er ansvarlig for etter preseptorisk lovgivning.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 2, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "14. Offentlige krav m.v. Kunden forplikter seg til ikke å benytte Utstyret i Kontraktsperioden uten først å ha forsikret seg om at det, for så vidt gjelder utstyr, verneinnretninger og lignende, oppfyller alle gjeldende forskrifter og krav som er fastsatt av myndighetene. SC er ikke ansvarlig dersom det foreligger eller oppstår hindringer i bruken av Utstyret som følge av forskrifter og/eller myndighetsbeslutninger. Slike hindringer kan ikke danne grunnlag for erstatningskrav fra Kunden mot SC, og kan heller ikke påberopes som grunnlag for helt eller delvis fritak fra forpliktelser som påhviler Kunden.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 2, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "15. Kontraktstid, forlengelse og tilbakelevering Kontraktstiden er angitt på forsiden av avtalen. Dersom Kunden ikke senest 90 dager før utløpet av den avtalte Kontraktsperioden eller, dersom forlengelse er innvilget, før utløpet av forlengelsesperioden, skriftlig har varslet SC om at Utstyret vil bli tilbakelevert, forlenges Kontraktsperioden automatisk med 12 måneder, heretter kalt forlengelsesperioden, for hver gang slikt varsel ikke er gitt. I forlengelsesperioden har SC rett til å justere Årsprisen til det prisnivået som gjelder på det aktuelle tidspunktet for det tilsvarende Utstyret. For forlengelsesperioden gjelder samme betalingsterminer og forfallstidspunkter som for den opprinnelige Kontraktsperioden. I forlengelsesperioden skal vilkårene i denne avtalen for øvrig være uendret. Kunden skal senest 30 dager etter utløpet av Kontraktsperioden returnere Utstyret, inkludert alt tilbehør som ladekabel, bæretillbehør osv., for egen regning og risiko til et sted i Norge som er utpekt av SC. Dersom Utstyret ved tilbakelevering er i dårligere stand enn det som er normalt med hensyn til alder og normal bruk, har SC rett til å utbedre slike mangler ved Utstyrets tilstand for Kundens regning og bekostning. Dersom tilbakelevering av Utstyret med alt tilbehør ikke skjer til rett tid, vil Kunden bli belastet gjenanskaffelsesverdi.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 2, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "16. SCs rett til oppsigelse av avtalen, erstatning SC har rett til å med umiddelbar virkning si opp avtalen og på Kundens kostnad ta tilbake Utstyret dersom: a) Kunden ikke betaler Årsprisen, forfalte skatter, avgifter eller andre kostnader etter avtalen og slik forsinkelse vedvarer i mer enn 30 dager; b) Kunden innstiller sine betalinger, søker om eller det åpnes rekonstruksjon eller konkurs, begjærer eller innleder akkordforhandling, likvideres eller på annen måte etter SCs vurdering med rimelighet kan fryktes å være insolvent; c) Kunden forsømmer Utstyret eller nekter å la SC besiktige Utstyret; d) det etter SCs oppfatning er rimelig grunn til å anta at Kunden ikke vil oppfylle sine forpliktelser overfor SC i henhold til avtalen eller annen forpliktelse overfor SC; e) Kunden på annen måte tilsidesetter noen bestemmelse i avtalen; f) Kunden ikke svarer på SCs henvendelser eller på annen måte ikke bidrar til SCs løpende kundekunnskap. Dersom det foreligger oppsigelsesgrunn som beskrevet ovenfor, har SC også rett til å si opp andre avtaler med Kunden. Dersom avtalen sies opp av SC av grunner som nevnt ovenfor, har SC, i tillegg til Årspris og andre skyldige beløp under denne avtalen, også rett til erstatning som, med mindre annen skade kan påvises, minst tilsvarer prisen for den gjenværende Kontraktsperioden, alt neddiskontert til nåverdi på tidspunktet for oppsigelsen. Nåverdien skal diskonteres med en rentesats som tilsvarer gjeldende NIBOR 30 dager. I tillegg skal alle kostnader ved tilbakelevering av Utstyret legges til. Erstatningen skal reduseres med det proveny, eksklusive merverdiavgift, som SC mottar etter fradrag av salgs-, istandsettelses- og andre kostnader ved salg av Utstyret, dersom dette er mulig etter SCs skjønn.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "17. Overdragelse Denne avtalen og rettighetene og forpliktelsene i henhold til denne kan ikke overdras av noen av Partene, verken helt eller delvis, uten skriftlig forhåndssamtykke fra den andre Parten.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "18. Endringer og tillegg Endringer og tillegg til denne avtalen skal være skriftlige og undertegnet av begge Parter.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "19. Meldinger Kunden skal umiddelbart underrette SC om enhver adresse- og/eller navneendring. Meddelelser vedrørende denne avtalen som sendes fra SC til Kunden, skal anses å ha kommet frem til Kunden senest den fjerde dagen etter avsendelse, dersom brevet er sendt til den adresse som er angitt i avtalen eller annen adresse som er kjent for SC. Meldinger sendt per e-post skal anses å ha kommet frem til Kunden umiddelbart.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "20. Ansvarsbegrensning SC er ikke ansvarlig overfor Kunden for skade som skyldes lovbestemt påbud, myndighetshandling, krigshandling, streik, blokade, boikott, lockout eller annen lignende omstendighet utenfor SCs kontroll. Dette forbeholdet gjelder uavhengig av om SC selv er part i, utsettes for eller iverksetter slike tiltak. Dersom SC er forhindret fra å oppfylle sine forpliktelser overfor Kunden som følge av noen av de ovennevnte omstendigheter, har SC rett til å utsette oppfyllelsen av disse til hindringen er opphørt. SC er heller ikke forpliktet til å erstatte skade som oppstår dersom SC har utvist normal aktsomhet. SC er ikke i noe tilfelle forpliktet til å betale erstatning for indirekte tap eller konsekvenstap (tapt fortjeneste, produksjonstap osv.).",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "21. Lovvalg og verneting Norsk lov skal gjelde for denne avtalen. Tvister som oppstår i forbindelse med denne avtalen skal avgjøres endelig ved voldgift administrert av SCC Arbitration Institute (Stockholms Handelskammares Skiljedomsinstitutt) (SCC). Regler for Förenklat Skiljeförfarande skal gjelde, med mindre SCC, med hensyn til sakens kompleksitet, tvistegjenstandens verdi og andre omstendigheter, beslutter at Regler for voldgift skal gjelde. I sistnevnte tilfelle skal SCC også bestemme om voldgiftsretten skal bestå av én eller tre voldgiftsdommere. Voldgiftsrettens sete skal være Stockholm. Språket i voldgiftsforhandlingene skal være svensk. Voldgiftsbehandling som er igangsatt i henhold til denne voldgiftsklausulen er underlagt konfidensialitet. Taushetsplikten gjelder for all informasjon som fremkommer under behandlingen, samt for enhver avgjørelse eller dom som treffes i forbindelse med behandlingen.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "22. Informasjon om behandling av personopplysninger For Kundens personopplysninger er SC behandlingsansvarlig. SC behandler Kundens personopplysninger i henhold til den til enhver tid gjeldende personvernerklæringen, som er tilgjengelig på SCs nettsider. SC rapporterer informasjonen om behandlingen av personopplysninger til Kunden i forbindelse med at Kunden inngår avtalen, der Kunden godkjenner SCs behandling av personopplysninger. Kunden er innforstått med at SC, for å oppfylle krav til dokumentasjon for blant annet forebygging av hvitvasking av penger, må behandle Kundens personopplysninger.",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
      [
        {
          content: "23. Programvare og lisensrettigheter Utstyret omfatter programvare/licensrettigheter til SecurCloud (heretter \"Programvaren\"). SC aksepterer og bekrefter gjennom denne avtalen at Programvaren kan benyttes av Kunden i henhold til vilkårene i denne avtalen. SC er under enhver omstendighet ikke ansvarlig for skade som følge av Kundens bruk av Programvaren (tap av data, direkte tap eller skade, indirekte tap eller skade eller konsekvens- eller følgeskade).",
          styles: { fontSize: content_fsize, fontStyle: "normal", halign: "justify", cellPadding: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 } },
        },
      ],
    ];

    const terms = isNorwegian ? norwegianTerms : swedishTerms;

    doc.autoTable({
      startY: 5,
      showHead: "never",
      theme: "plain",
      body: terms,
    });

    return doc;
  },

  /**
   * Returns the default (unedited) text for every editable paragraph in the
   * framework agreement PDF, keyed by paragraph ID.  Pass the same
   * `agreement` and `customer` objects as you would to
   * `generateFrameworkAgreementPDF`; some defaults embed dynamic values
   * (customer name, org.nr, address).
   */
  getPdfTextDefaults(agreement, customer) {
    const customerName = agreement.customer_name || '';
    const orgNr = (customer && customer.company_number) || '';
    const address = (customer && customer.address) || '';
    const zipCode = (customer && customer.zip_code) || '';
    const city = (customer && customer.city) || '';
    const zipCity = [zipCode, city].filter(Boolean).join(' ');
    const fullAddress = [address, zipCity].filter(Boolean).join(', ');
    return {
      intro_1: 'Detta Samarbetsavtal ("Avtalet") har ingåtts mellan:',
      intro_2: 'Securdia AB, org.nr 559270–2087, med adress Ekbacksvägen 22, 168 69 Bromma ("Securdia"),',
      intro_3: 'och',
      intro_4: `${customerName}, org.nr ${orgNr}, med adress ${fullAddress} ("Kunden").`,
      intro_5: 'Securdia och Kunden benämns var för sig "Part" och gemensamt "Parterna".',
      s1_1: '1.1 Parterna önskar genom detta Avtal etablera en långsiktig, flexibel och samarbetsinriktad avtalsram för Kundens behov av säkerhetsteknik, säkerhetsrelaterade tjänster och utbildningsinsatser. Avtalet gäller för Kundens alla tillhörande bolag, utan någon begränsning till land.',
      s1_2: '1.2 Avtalets syfte är att fastställa de övergripande kommersiella och juridiska villkoren för samarbetet mellan Parterna. Konkreta beställningar, leveranser, implementationer, utbildningar och övriga uppdrag ska regleras närmare i separata leveransavtal, avropsavtal, beställningar, ordererkännanden eller motsvarande handlingar (gemensamt "Leveransavtal").',
      s1_3: '1.3 Avtalet ska främja ett nära samarbete mellan Parterna med målsättningen att bidra till en trygg, säker och väl fungerande arbetsmiljö för Kundens medarbetare och verksamhet.',
      s1_4: '1.4 Securdia ska inom ramen för samarbetet verka som en strategisk partner till Kunden och, när så är lämpligt och möjligt, bistå Kunden i samband med övergång från annan leverantör, inklusive stöd avseende uppsägningar, planering, omallokering av utrustning samt hantering av slutfakturor och praktiska övergångsfrågor. Sådant bistånd utförs i samverkan med Kunden och förutsätter att Kunden tillhandahåller nödvändiga underlag och fullmakter där så krävs.',
      s2_1: '2.1 Detta Avtal omfattar Securdias erbjudande avseende uthyrning, tillhandahållande, implementation, support, utbildning och relaterade tjänster inom säkerhetsområdet, innefattande men inte begränsat till:',
      s2_1_list: 'a) mobila personlarm\nb) larmdisplayer\nc) radiofyrar\nd) SecurCloud\ne) skalskydd, kameror och annan säkerhetsteknik,\nf) utbildning inom hot och våld,\ng) support-, rådgivnings- och samordningsinsatser kopplade till ovanstående.',
      s2_2: '2.2 Detta Avtal innebär inte någon skyldighet för Kunden att uppnå viss inköpsvolym, viss minsta beställning eller visst ekonomiskt åtagande, om inte annat uttryckligen anges i ett enskilt Leveransavtal.',
      s2_3: '2.3 Detta Avtal innebär inte heller någon exklusivitet för Kunden. Kunden äger således rätt att köpa eller hyra motsvarande eller liknande produkter och tjänster från annan leverantör. Kunden ska dock, i den mån det är praktiskt, kommersiellt och verksamhetsmässigt möjligt, överväga att i första hand anskaffa sådan utrustning och sådana tjänster från Securdia inom Avtalets tillämpningsområde.',
      s2_4: '2.4 För undvikande av missförstånd innebär Avtalet inte någon skyldighet för Securdia att acceptera varje enskild beställning. Varje uppdrag, leverans eller hyresförhållande förutsätter ett särskilt Leveransavtal eller annan bekräftad beställning.',
      s2_5: '2.5 Parterna är vidare överens om att samarbetet kan komma att omfatta även verksamhet utanför Sverige, och att Securdia inom ramen för sin internationella organisation ska kunna stödja Kunden även i sådana sammanhang.',
      s3_1: '3.1 Detta Avtal utgör Parternas allmänna och övergripande samarbetsvillkor.',
      s3_2: '3.2 För varje enskild beställning ska Parterna träffa Leveransavtal som reglerar de särskilda villkor som gäller för just den leveransen, inklusive exempelvis leveransomfattning, leveransadress, installation, projektspecifika tidplaner, särskilda servicenivåer, eventuella personuppgiftsfrågor, särskilda säkerhetskrav, pris eller andra kommersiella villkor.',
      s3_3: '3.3 Om det förekommer motstridiga uppgifter mellan detta Avtal och ett Leveransavtal ska följande ordning gälla, om inte annat uttryckligen anges i Leveransavtalet:',
      s3_3_list: 'a) skriftligt undertecknat Leveransavtal,\nb) detta Avtal,\nc) övriga bilagor eller övrig dokumentation.',
      s3_4: '3.4 Leveransavtalet ska alltid anses komplettera detta Avtal. I den mån ett Leveransavtal saknar reglering i viss fråga ska detta Avtal tillämpas. Om ett Leveransavtal innehåller avvikande eller mer specifik reglering ska Leveransavtalet gälla i den delen.',
      s4_1: '4.1 Detta Avtal träder i kraft när det undertecknats av båda Parter och gäller därefter under en initial period om fem (5) år.',
      s4_2: '4.2 Kunden ska därefter ha rätt att förlänga Avtalet med upp till två (2) ytterligare år ("Optionsperioden"). Förlängning ska ske genom skriftligt meddelande till Securdia senast tre (3) månader före den initiala avtalsperiodens utgång.',
      s4_3: '4.3 Om Parterna fortsätter samarbetet efter Avtalets utgång utan att nytt avtal har träffats, ska detta Avtal tills vidare gälla för redan pågående Leveransavtal till dess att dessa upphört eller ersatts, om inte Parterna skriftligen kommer överens om annat.',
      s5_1: '5.1 Samtliga beställningar från Kunden ska dokumenteras i ett Leveransavtal, beställningsunderlag, skriftlig orderbekräftelse eller annan av Parterna godkänd skriftlig handling.',
      s5_2: '5.2 Ett Leveransavtal ska minst ange:',
      s5_2_list: 'a) vilka produkter och/eller tjänster som omfattas,\nb) antal, omfattning eller abonnemang,\nc) tillämplig prisbilaga eller offert,\nd) leverans- eller startdatum,\ne) eventuell minsta hyresperiod eller annan särskild kommersiell förutsättning,\nf) eventuella särskilda krav avseende säkerhet, sekretess, personuppgiftsbehandling, installation eller utbildning.',
      s5_3: '5.3 Beställning är bindande för Parterna först när den har bekräftats skriftligen av Securdia eller annars framgår av ett undertecknat Leveransavtal.',
      s6_1: '6.1 Om inte annat följer av ett särskilt Leveransavtal eller en offert, ska följande riktpriser/grundpriser gälla inom ramen för detta Avtal, exklusive mervärdesskatt:',
      s6_2: '6.2 Samtliga priser anges exklusive mervärdesskatt, offentliga avgifter, frakt, installation, resor och andra tillkommande kostnader, om inte annat uttryckligen framgår av Leveransavtal eller offert.',
      s6_3: '6.3 Vid större volymer, särskilda implementationer, kundunika lösningar eller samlade åtaganden kan Parterna komma överens om särskild prisbilaga eller särskilda rabatter i respektive Leveransavtal.',
      s6_4: '6.4 Hårdvara som tillhandahålls, hyrs ut eller utlånas av Securdia kan, om inte annat anges i Leveransavtal, levereras med försäkringsskydd. Kostnaden för sådant försäkringsskydd bekostas av Kunden och debiteras enligt vid var tid gällande prislista, offert eller Leveransavtal.',
      s7_1: '7.1 Securdia äger rätt att justera priserna högst en (1) gång per kalenderår.',
      s7_2: '7.2 Prisjustering ska ske med utgångspunkt i förändringen av Arbetskostnadsindex (AKI) eller motsvarande ersättningsindex om AKI upphör eller väsentligen förändras.',
      s7_4: '7.4 Om ett Leveransavtal innehåller särskild reglering om prisjustering ska sådan reglering äga företräde framför denna bestämmelse.',
      s8_1: '8.1 Securdia fakturerar kvartalsvis i förskott, om inte annat uttryckligen framgår av Leveransavtal eller skriftlig överenskommelse.',
      s8_2: '8.2 Betalning ska erläggas inom trettio (30) dagar från fakturadatum.',
      s8_3: '8.3 Vid dröjsmål med betalning har Securdia rätt att debitera dröjsmålsränta enligt räntelagen (1975:635) samt påminnelse- och inkassoavgifter enligt lag.',
      s8_4: '8.4 Kunden är skyldig att betala ostridiga belopp även om del av faktura bestrids. Invändning mot faktura ska framställas skriftligen inom skälig tid och senast inom tio (10) dagar från mottagandet.',
      s8_5: '8.5 Securdia har rätt, men ingen skyldighet, att helt eller delvis överlåta eller pantsätta sin fordran enligt detta Avtal eller Leveransavtal till finansbolag eller annan tredje man. Sådan överlåtelse påverkar inte Kundens rättigheter eller skyldigheter enligt Avtalet i övrigt.',
      s9_1: '9.1 Securdia ansvarar för att avtalade produkter och tjänster levereras i enlighet med respektive Leveransavtal.',
      s9_2: '9.2 Kunden ansvarar för att lämna Securdia sådan information, tillgång till lokaler, tekniska förutsättningar, kontaktpersoner, beslut, samordning och andra medverkansinsatser som skäligen krävs för att Securdia ska kunna fullgöra sina åtaganden.',
      s9_3: '9.3 Om leverans eller installation försenas eller försvåras till följd av förhållande på Kundens sida, har Securdia rätt till skälig tidsförlängning samt ersättning för styrkta och skäliga merkostnader.',
      s9_4: '9.4 Securdias åtaganden avser tillhandahållande av produkter, tjänster, utbildning och säkerhetsrelaterat stöd. Securdia ansvarar inte för att Kunden uppnår visst resultat, viss myndighetsbedömning eller en fullständigt riskfri verksamhet, om inte sådan resultatgaranti uttryckligen avtalats skriftligen.',
      s10_1: '10.1 Om inte annat uttryckligen avtalats förblir uthyrd eller utlånad utrustning Securdias egendom.',
      s10_2: '10.2 Kunden ska hantera utrustningen omsorgsfullt, följa Securdias instruktioner och inte utan Securdias skriftliga medgivande göra ingrepp, ändringar, ombyggnader eller flytt av installerad utrustning som kan påverka funktion, säkerhet eller garanti.',
      s10_3: '10.3 Kunden ansvarar för förlust av eller skada på utrustning som inte beror på normalt slitage, materialfel eller omständigheter som Securdia ansvarar för.',
      s10_4: '10.4 Kunden ska utan oskäligt dröjsmål underrätta Securdia om fel, driftstörning, skada, förlust eller annan omständighet som påverkar utrustningen eller tjänsten.',
      s10_5: '10.5 Hårdvara som tillhandahålls, hyrs ut eller utlånas av Securdia kan omfattas av försäkring avseende skada på eller förlust av utrustningen. Sådan försäkring bekostas av Kunden och debiteras separat eller som del av avtalad månadsavgift, enligt vad som framgår av Leveransavtal, prisbilaga, offert eller annan skriftlig överenskommelse.',
      s10_6: '10.6 Försäkringen är verksamhetsspecifik och avser endast den verksamhet, enhet, plats eller organisation som anges i relevant Leveransavtal eller annars skriftligen överenskommits mellan Parterna. Försäkringen får inte överlåtas, flyttas eller tillämpas på annan verksamhet utan Securdias skriftliga medgivande.',
      s10_7: '10.7 Kunden har rätt att när som helst säga upp försäkringen genom skriftligt meddelande till Securdia. Uppsägningen påverkar inte Kundens ansvar enligt detta Avtal eller relevant Leveransavtal för förlust av eller skada på utrustning som inträffar efter att försäkringsskyddet upphört.',
      s11_1: '11.1 Securdia ska tillhandahålla support, service och utbildning i den omfattning som följer av respektive Leveransavtal eller annars av Securdias vid var tid gällande servicebeskrivning.',
      s11_2: '11.2 Parterna ska samverka lojalt och skyndsamt för att identifiera, prioritera och hantera säkerhetsrelaterade behov, förändringar och förbättringsåtgärder i Kundens verksamhet.',
      s11_3: '11.3 Securdia ska i sitt arbete verka affärsetiskt, professionellt och med beaktande av Kundens behov av trygghet, integritet och en god arbetsmiljö.',
      s12_1: '12.1 Securdia ska, inom ramen för Parternas samarbete, verka som strategisk partner till Kunden i frågor som rör säkerhetsteknik, personlarm, relaterade processer och utbildning inom hot och våld.',
      s12_2: '12.2 Om Kunden använder utrustning eller tjänster från annan leverantör får Securdia, efter överenskommelse, bistå Kunden med planering och administration i samband med övergång till Securdia, inklusive stöd vid uppsägning av befintliga avtal, genomgång av konkurrerande utrustning, omallokering, koordinering samt rådgivning kring slutfakturor och överlappande avtalsperioder.',
      s12_3: '12.3 Securdias bistånd enligt denna punkt innebär inte att Securdia övertar Kundens legala betalningsansvar eller annat avtalsansvar gentemot tredje man, om inte Parterna uttryckligen avtalar om detta skriftligen.',
      s13_1: '13.1 Oavsett vad som anges i enskilt Leveransavtal om avtalstid ska Kunden ha rätt att säga upp sådant Leveransavtal med sex (6) månaders uppsägningstid.',
      s13_2: '13.2 Innan uppsägning sker ska Kunden, i den mån det är praktiskt, tekniskt och verksamhetsmässigt möjligt, i första hand verka för att omallokera aktuella personlarm eller annan relevant utrustning inom den egna verksamheten eller organisationen.',
      s13_3: '13.3 Kunden får under en och samma tolvmånadersperiod inte säga upp mer än tjugo (20) procent av det totala antalet aktiva personlarm enligt gällande Leveransavtal, såvida inte annat skriftligen överenskommes eller särskilda skäl föreligger, såsom väsentlig verksamhetsförändring, myndighetsbeslut, omorganisation, budgetbeslut eller annan jämförbar omständighet utanför Kundens normala kontroll.',
      s13_4: '13.4 Begränsningen i punkt 13.3 ska tillämpas på ett affärsmässigt och samarbetsinriktat sätt. Parterna ska i första hand söka en praktisk lösning som minimerar störningar för Kundens verksamhet och samtidigt beaktar Securdias planerings- och investeringsförutsättningar.',
      s13_5: '13.5 Uppsägning ska ske skriftligen.',
      s14_1: '14.1 Vardera Parten har rätt att säga upp detta Avtal med omedelbar verkan om den andra Parten:',
      s14_1_list: 'a) väsentligen bryter mot Avtalet och inte vidtar rättelse inom trettio (30) dagar från skriftlig anmodan,\nb) inställer sina betalningar, inleder företagsrekonstruktion, träder i likvidation, försätts i konkurs eller annars skäligen kan antas vara på obestånd,\nc) agerar i strid med tillämplig lag eller på ett sätt som medför väsentlig risk för den andra Partens renommé eller rättsliga ställning.',
      s14_2: '14.2 Avtalets upphörande påverkar inte redan ingångna Leveransavtal, vilka ska fortsätta att gälla enligt sina villkor, om inte annat uttryckligen anges i uppsägningen eller särskilt överenskommes.',
      s15_1: '15.1 Part förbinder sig att inte utan den andra Partens skriftliga medgivande till tredje man lämna ut information om den andra Partens verksamhet, affärsförhållanden, teknik, prismodeller, säkerhetslösningar, kundförhållanden eller andra uppgifter som är att anse som konfidentiella eller som Part skäligen borde förstå är konfidentiella ("Konfidentiell Information").',
      s15_2: '15.2 Sekretessåtagandet gäller inte information som:',
      s15_2_list: 'a) var allmänt känd eller kommit till allmän kännedom på annat sätt än genom brott mot detta Avtal,\nb) Part kan visa att denne lagligen erhållit från tredje man utan sekretessförpliktelse,\nc) Part är skyldig att lämna ut enligt lag, förordning, domstolsbeslut, myndighetsbeslut eller annan bindande föreskrift.',
      s15_3: '15.3 Om Part är skyldig att lämna ut Konfidentiell Information enligt punkt 15.2 c) ska Parten, i den mån det är tillåtet, utan dröjsmål underrätta den andra Parten innan utlämnande sker.',
      s15_4: '15.4 Vardera Parten ska säkerställa att endast sådana anställda, konsulter eller underleverantörer som behöver informationen för Avtalets fullgörande får tillgång till den, och att dessa omfattas av sekretess som minst motsvarar detta Avtal.',
      s15_5: '15.5 Sekretessåtagandet gäller under Avtalets löptid och under fem (5) år därefter. För information som utgör företagshemlighet eller säkerhetskänslig information ska sekretessåtagandet gälla så länge informationen förblir skyddsvärd.',
      s16_1: '16.1 I den mån Securdia behandlar personuppgifter för Kundens räkning ska Parterna, innan sådan behandling påbörjas, ingå särskilt personuppgiftsbiträdesavtal om det krävs enligt tillämplig dataskyddslagstiftning.',
      s16_2: '16.2 Vardera Parten ansvarar för att den egna behandlingen av personuppgifter sker i enlighet med tillämplig lagstiftning, inklusive dataskyddsförordningen (GDPR), kompletterande svensk lagstiftning och i förekommande fall regler om kamerabevakning.',
      s16_3: '16.3 Kunden ansvarar för att nödvändiga rättsliga grunder, information till registrerade, interna instruktioner och eventuella fackliga eller arbetsrättsliga bedömningar finns på plats i Kundens verksamhet när Kundens användning av produkter eller tjänster innebär behandling av personuppgifter eller någon form av övervakning eller kontroll.',
      s17_1: '17.1 Samtliga immateriella rättigheter hänförliga till Securdias produkter, mjukvara, dokumentation, utbildningsmaterial, koncept, metoder och tekniska lösningar tillhör Securdia eller dess licensgivare.',
      s17_2: '17.2 Kunden erhåller endast den nyttjanderätt som uttryckligen följer av detta Avtal eller tillämpligt Leveransavtal.',
      s17_3: '17.3 Kunden får inte kopiera, modifiera, dekompilera, vidarelicensiera eller på annat sätt förfoga över Securdias material eller system i vidare mån än vad som följer av tvingande lag eller skriftligt medgivande från Securdia.',
      s18_1: '18.1 Part ansvarar för direkt skada som vållats den andra Parten genom vårdslöshet eller avtalsbrott.',
      s18_2: '18.2 Part ansvarar inte för indirekt skada, såsom utebliven vinst, produktionsbortfall, inkomstförlust, förlust av data, goodwillförlust eller annan följdskada, om inte skadan orsakats uppsåtligen eller genom grov vårdslöshet.',
      s18_3: '18.3 Securdias sammanlagda ansvar enligt detta Avtal och ett enskilt Leveransavtal ska, om inte uppsåt eller grov vårdslöshet föreligger, vara begränsat till ett belopp motsvarande tolv (12) månaders avgifter enligt det Leveransavtal som skadan hänför sig till.',
      s18_4: '18.4 Begränsningarna i denna punkt gäller inte för ansvar enligt punkt 15 (Sekretess), punkt 16 (Personuppgifter och integritet), intrång i immateriella rättigheter, eller för skada som inte får begränsas enligt tvingande lag.',
      s19_1: '19.1 Part ska vara befriad från påföljd för underlåtenhet att fullgöra viss förpliktelse enligt Avtalet om underlåtenheten har sin grund i omständighet utanför Partens skäliga kontroll och som Parten inte skäligen kunnat förutse vid Avtalets ingående eller undvika eller övervinna, såsom exempelvis krig, krigsliknande händelse, terroristangrepp, upplopp, sabotage, brand, explosion, naturkatastrof, extremväder, epidemi, pandemi, myndighetsåtgärd, elavbrott, tele- eller datakommunikationsstörning, cyberangrepp, strejk, lockout eller annan arbetskonflikt.',
      s19_2: '19.2 Part som åberopar force majeure ska utan oskäligt dröjsmål skriftligen underrätta den andra Parten om händelsen, dess förväntade påverkan och när fullgörelse beräknas kunna återupptas.',
      s19_3: '19.3 Om force majeure varar längre än nittio (90) dagar i följd har vardera Parten rätt att skriftligen säga upp den berörda delen av Avtalet eller berört Leveransavtal utan ansvar för skadestånd eller annan ersättning, med undantag för betalningsskyldighet för redan utförd prestation.',
      s20_1: '20.1 Parterna ska bedriva sin verksamhet i enlighet med tillämplig lag, god affärssed och höga etiska krav.',
      s20_2: '20.2 Securdia ska i sitt arbete sträva efter att vara en ansvarsfull, etisk och långsiktig samarbetspartner för Kunden.',
      s20_3: '20.3 Ingen Part får erbjuda, utlova eller ge otillbörlig förmån i samband med Avtalet. Parterna ska särskilt beakta de krav som gäller vid samarbete med offentliga aktörer och kommuner.',
      s21_1: '21.1 Om Kunden är en upphandlande myndighet eller enhet gäller detta Avtal endast i den utsträckning det är förenligt med tillämplig upphandlingslagstiftning och Kundens upphandlingsdokument.',
      s21_2: '21.2 För sådan Kund förutsätter beställningar och Leveransavtal att avrop, beställning eller annan anskaffning sker i enlighet med tillämpligt ramavtal, upphandlingskontrakt, direktupphandling eller annan tillåten anskaffningsform.',
      s21_3: '21.3 Om någon bestämmelse i detta Avtal skulle strida mot tvingande upphandlingsrättslig reglering eller bindande upphandlingsdokument ska sådan bestämmelse justeras eller lämnas utan avseende i den utsträckning som krävs för att uppfylla tillämpliga regler, utan att Avtalets övriga delar påverkas.',
      s22_1: '22.1 Kunden får inte utan Securdias skriftliga medgivande överlåta sina rättigheter eller skyldigheter enligt detta Avtal, helt eller delvis, till annan part, med undantag för överlåtelse inom samma kommunala organisation, koncern eller till annan verksamhet som övertar Kundens berörda verksamhet, under förutsättning att sådan mottagare kan förväntas fullgöra Kundens skyldigheter enligt Avtalet.',
      s22_2: '22.2 Securdia får överlåta detta Avtal till annat bolag inom samma koncern eller i samband med företagsöverlåtelse, fusion eller liknande omstrukturering, under förutsättning att Kundens rättigheter enligt Avtalet inte försämras i väsentligt avseende.',
      s22_3: '22.3 Bestämmelsen i denna punkt påverkar inte Securdias rätt enligt punkt 8.5 att överlåta eller pantsätta fordran.',
      s23_1: '23.1 Meddelanden enligt detta Avtal ska ske skriftligen via bud, rekommenderat brev, e-post eller annan överenskommen elektronisk kommunikation till de kontaktpersoner eller adresser som anges i Avtalet eller senare meddelas skriftligen.',
      s23_2: '23.2 Meddelande ska anses ha kommit mottagaren tillhanda:',
      s23_2_list: 'a) om överlämnat med bud: vid överlämnandet,\nb) om sänt med rekommenderat brev: tre (3) arbetsdagar efter avsändandet,\nc) om sänt via e-post: när det elektroniskt registrerats som avsänt, under förutsättning att avsändaren inte erhållit felmeddelande och att meddelandet skickats under normal kontorstid, annars nästkommande arbetsdag.',
      s24_intro: 'Securdias huvudsakliga kontaktpersoner för detta Avtal är:',
      s24_post: 'Kunden ska utse motsvarande kontaktperson(er) i samband med Avtalets undertecknande eller i särskilt Leveransavtal.',
      s25_1: '25.1 Ändringar och tillägg till detta Avtal ska för att vara giltiga vara skriftliga och undertecknade av behöriga företrädare för båda Parter.',
      s25_2: '25.2 Detta gäller dock inte ändringar av kontaktuppgifter, administrativa rutiner eller motsvarande som får meddelas i enlighet med punkt 23.',
      s26_1: '26.1 Om någon bestämmelse i detta Avtal skulle befinnas ogiltig eller inte kunna göras gällande, ska detta inte innebära att Avtalet i dess helhet är ogiltigt.',
      s26_2: '26.2 I sådan situation ska bestämmelsen justeras i den utsträckning som krävs för att den ska bli giltig och ge ett resultat som så långt som möjligt motsvarar bestämmelsens ursprungliga syfte.',
      s27_1: '27.1 Detta Avtal, tillsammans med tillhörande Leveransavtal, utgör Parternas fullständiga reglering av de frågor som Avtalet avser och ersätter alla tidigare muntliga och skriftliga överenskommelser, utfästelser och diskussioner i samma ämne.',
      s28_1: '28.1 Detta Avtal ska tolkas och tillämpas enligt svensk materiell rätt.',
      s28_2: '28.2 Tvist i anledning av detta Avtal ska i första hand lösas genom förhandling mellan Parterna.',
      s28_3: '28.3 Om Parterna inte kan enas inom trettio (30) dagar från det att tvisten skriftligen påtalats, ska tvisten avgöras av allmän domstol med [Stockholms tingsrätt] som första instans, om inte annat följer av tvingande lag eller särskilt överenskommes skriftligen.',
    };
  },

  /** Returns the section/paragraph structure used by the preview sidebar accordion. */
  getPdfSections() {
    return [
      { id: 'intro', label: 'Inledning', paragraphs: [
        { key: 'intro_1', label: 'Inledande mening' },
        { key: 'intro_2', label: 'Securdia (part)' },
        { key: 'intro_4', label: 'Kund (part)' },
        { key: 'intro_5', label: 'Partsdefinition' },
      ]},
      { id: 's1', label: '1. Bakgrund och syfte', paragraphs: [
        { key: 's1_1', label: '1.1' }, { key: 's1_2', label: '1.2' },
        { key: 's1_3', label: '1.3' }, { key: 's1_4', label: '1.4' },
      ]},
      { id: 's2', label: '2. Avtalets omfattning', paragraphs: [
        { key: 's2_1', label: '2.1' },
        { key: 's2_1_list', label: '2.1 (lista)', isList: true },
        { key: 's2_2', label: '2.2' }, { key: 's2_3', label: '2.3' },
        { key: 's2_4', label: '2.4' }, { key: 's2_5', label: '2.5' },
      ]},
      { id: 's3', label: '3. Avtalshierarki', paragraphs: [
        { key: 's3_1', label: '3.1' }, { key: 's3_2', label: '3.2' },
        { key: 's3_3', label: '3.3' },
        { key: 's3_3_list', label: '3.3 (lista)', isList: true },
        { key: 's3_4', label: '3.4' },
      ]},
      { id: 's4', label: '4. Avtalstid', paragraphs: [
        { key: 's4_1', label: '4.1' }, { key: 's4_2', label: '4.2' },
        { key: 's4_3', label: '4.3' },
      ]},
      { id: 's5', label: '5. Beställningar och Leveransavtal', paragraphs: [
        { key: 's5_1', label: '5.1' }, { key: 's5_2', label: '5.2' },
        { key: 's5_2_list', label: '5.2 (lista)', isList: true },
        { key: 's5_3', label: '5.3' },
      ]},
      { id: 's6', label: '6. Produkter, tjänster och prislista', paragraphs: [
        { key: 's6_1', label: '6.1' }, { key: 's6_2', label: '6.2' },
        { key: 's6_3', label: '6.3' }, { key: 's6_4', label: '6.4' },
      ]},
      { id: 's7', label: '7. Prisjustering', paragraphs: [
        { key: 's7_1', label: '7.1' }, { key: 's7_2', label: '7.2' },
        { key: 's7_4', label: '7.4' },
      ]},
      { id: 's8', label: '8. Fakturering och betalningsvillkor', paragraphs: [
        { key: 's8_1', label: '8.1' }, { key: 's8_2', label: '8.2' },
        { key: 's8_3', label: '8.3' }, { key: 's8_4', label: '8.4' },
        { key: 's8_5', label: '8.5' },
      ]},
      { id: 's9', label: '9. Leverans, installation och ansvarsfördelning', paragraphs: [
        { key: 's9_1', label: '9.1' }, { key: 's9_2', label: '9.2' },
        { key: 's9_3', label: '9.3' }, { key: 's9_4', label: '9.4' },
      ]},
      { id: 's10', label: '10. Utrustning, användning och vård', paragraphs: [
        { key: 's10_1', label: '10.1' }, { key: 's10_2', label: '10.2' },
        { key: 's10_3', label: '10.3' }, { key: 's10_4', label: '10.4' },
        { key: 's10_5', label: '10.5' }, { key: 's10_6', label: '10.6' },
        { key: 's10_7', label: '10.7' },
      ]},
      { id: 's11', label: '11. Support, service och utbildning', paragraphs: [
        { key: 's11_1', label: '11.1' }, { key: 's11_2', label: '11.2' },
        { key: 's11_3', label: '11.3' },
      ]},
      { id: 's12', label: '12. Strategiskt partnerskap och övergångsstöd', paragraphs: [
        { key: 's12_1', label: '12.1' }, { key: 's12_2', label: '12.2' },
        { key: 's12_3', label: '12.3' },
      ]},
      { id: 's13', label: '13. Uppsägning av Leveransavtal och omallokering', paragraphs: [
        { key: 's13_1', label: '13.1' }, { key: 's13_2', label: '13.2' },
        { key: 's13_3', label: '13.3' }, { key: 's13_4', label: '13.4' },
        { key: 's13_5', label: '13.5' },
      ]},
      { id: 's14', label: '14. Uppsägning av Avtalet i förtid', paragraphs: [
        { key: 's14_1', label: '14.1' },
        { key: 's14_1_list', label: '14.1 (lista)', isList: true },
        { key: 's14_2', label: '14.2' },
      ]},
      { id: 's15', label: '15. Sekretess', paragraphs: [
        { key: 's15_1', label: '15.1' }, { key: 's15_2', label: '15.2' },
        { key: 's15_2_list', label: '15.2 (lista)', isList: true },
        { key: 's15_3', label: '15.3' }, { key: 's15_4', label: '15.4' },
        { key: 's15_5', label: '15.5' },
      ]},
      { id: 's16', label: '16. Personuppgifter och integritet', paragraphs: [
        { key: 's16_1', label: '16.1' }, { key: 's16_2', label: '16.2' },
        { key: 's16_3', label: '16.3' },
      ]},
      { id: 's17', label: '17. Immateriella rättigheter', paragraphs: [
        { key: 's17_1', label: '17.1' }, { key: 's17_2', label: '17.2' },
        { key: 's17_3', label: '17.3' },
      ]},
      { id: 's18', label: '18. Ansvarsbegränsning', paragraphs: [
        { key: 's18_1', label: '18.1' }, { key: 's18_2', label: '18.2' },
        { key: 's18_3', label: '18.3' }, { key: 's18_4', label: '18.4' },
      ]},
      { id: 's19', label: '19. Force majeure', paragraphs: [
        { key: 's19_1', label: '19.1' }, { key: 's19_2', label: '19.2' },
        { key: 's19_3', label: '19.3' },
      ]},
      { id: 's20', label: '20. Etik och regelefterlevnad', paragraphs: [
        { key: 's20_1', label: '20.1' }, { key: 's20_2', label: '20.2' },
        { key: 's20_3', label: '20.3' },
      ]},
      { id: 's21', label: '21. Offentlig verksamhet och upphandling', paragraphs: [
        { key: 's21_1', label: '21.1' }, { key: 's21_2', label: '21.2' },
        { key: 's21_3', label: '21.3' },
      ]},
      { id: 's22', label: '22. Överlåtelse', paragraphs: [
        { key: 's22_1', label: '22.1' }, { key: 's22_2', label: '22.2' },
        { key: 's22_3', label: '22.3' },
      ]},
      { id: 's23', label: '23. Meddelanden', paragraphs: [
        { key: 's23_1', label: '23.1' }, { key: 's23_2', label: '23.2' },
        { key: 's23_2_list', label: '23.2 (lista)', isList: true },
      ]},
      { id: 's24', label: '24. Kontaktpersoner', paragraphs: [
        { key: 's24_intro', label: '24 (inledning)' },
        { key: 's24_post', label: '24 (avslutning)' },
      ]},
      { id: 's25', label: '25. Ändringar och tillägg', paragraphs: [
        { key: 's25_1', label: '25.1' }, { key: 's25_2', label: '25.2' },
      ]},
      { id: 's26', label: '26. Ogiltighet', paragraphs: [
        { key: 's26_1', label: '26.1' }, { key: 's26_2', label: '26.2' },
      ]},
      { id: 's27', label: '27. Fullständig reglering', paragraphs: [
        { key: 's27_1', label: '27.1' },
      ]},
      { id: 's28', label: '28. Tillämplig lag och tvist', paragraphs: [
        { key: 's28_1', label: '28.1' }, { key: 's28_2', label: '28.2' },
        { key: 's28_3', label: '28.3' },
      ]},
    ];
  },

  /**
   * Generate a framework agreement (Samarbetsavtal) PDF using jsPDF.
   * Recreates the SAMARBETSAVTAL template programmatically with dynamic
   * customer data and a variable-length guide-prices table.
   *
   * @param {Object} agreement - Framework agreement document.
   * @param {Object} [customer] - Customer document for org.nr and address.
   * @returns {jsPDF} The jsPDF doc instance (caller decides what to do with it).
   */
  generateFrameworkAgreementPDF(agreement, customer) {
    const doc = new jsPDF({ unit: "mm", format: "a4" });

    const isNorwegian = (agreement.country || "").toLowerCase() === "no";
    const currencyLocale = isNorwegian ? "nb-NO" : "sv-SE";
    const currencyCode = isNorwegian ? "NOK" : "SEK";

    const fmtPrice = (n) => {
      const num = Number(n);
      if (!Number.isFinite(num)) return "";
      return num.toLocaleString(currencyLocale, {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    };

    const customerName = agreement.customer_name || "";
    const contactPerson = agreement.customer_contact_person || "";
    const contactEmail = agreement.customer_contact_email || "";
    const contactPhone = agreement.customer_contact_phone || "";
    const agreementNumber = agreement.agreement_number || "";

    // Text override helper: returns the user's custom text for a paragraph
    // key if set, otherwise falls back to the built-in default.
    const defaults = this.getPdfTextDefaults(agreement, customer);
    const ov = agreement.text_overrides || {};
    const t = (key) => (ov[key] !== undefined && ov[key] !== '' ? ov[key] : defaults[key]) || '';
    const tList = (key) => t(key).split('\n').filter(Boolean);
    const hidden = new Set(agreement.hidden_sections || []);
    const secRows = (id, rows) => hidden.has(id) ? [] : rows;
    const hiddenParas = new Set(agreement.hidden_paragraphs || []);

    // Section renumber map: section id → new sequential number after hidden sections removed.
    // Only numbered sections (s1–s28) are counted; 'intro' is not numbered.
    const buildSectionRenumberMap = () => {
      const map = {};
      let counter = 0;
      for (const sec of this.getPdfSections()) {
        const numPart = sec.id.replace(/^s/, '');
        if (!/^\d+$/.test(numPart)) continue;
        if (!hidden.has(sec.id)) {
          counter++;
          map[sec.id] = counter;
        }
      }
      return map;
    };
    const sectionNumMap = buildSectionRenumberMap();

    // Returns a bodyHeading row with the section number replaced by the renumbered value.
    const secHeading = (id, label) => {
      const newNum = sectionNumMap[id];
      if (!newNum) return bodyHeading(label);
      return bodyHeading(label.replace(/^\d+/, String(newNum)));
    };

    // Paragraph renumber map: para key → new "N.M" label using the new section number.
    const buildRenumberMap = () => {
      const map = {};
      for (const sec of this.getPdfSections()) {
        const numPart = sec.id.replace(/^s/, '');
        if (!/^\d+$/.test(numPart)) continue;
        const newSecNum = sectionNumMap[sec.id];
        if (!newSecNum) continue; // whole section is hidden
        let counter = 0;
        for (const para of sec.paragraphs) {
          if (para.isList) continue;
          if (!hiddenParas.has(para.key)) {
            counter++;
            map[para.key] = `${newSecNum}.${counter}`;
          }
        }
      }
      return map;
    };
    const renumberMap = buildRenumberMap();
    // Like t() but replaces the leading "N.M " number prefix with the renumbered one.
    const rt = (key) => {
      const text = t(key);
      if (!renumberMap[key]) return text;
      return text.replace(/^\d+\.\d+\s/, renumberMap[key] + ' ');
    };
    // Returns [bodyPara(rt(key))] when visible, [] when hidden.
    const paraRow = (key) => hiddenParas.has(key) ? [] : [bodyPara(rt(key))];
    // Returns sublist rows only when the parent paragraph is visible.
    const listRow = (listKey, parentKey) => hiddenParas.has(parentKey) ? [] : bodySublist(tList(listKey));

    // Page chrome — Securdia address block (left) + logo (right) per the
    // Word template header. Page numbers are drawn AFTER all content has
    // been laid out (see loop near the end of this method) so the total
    // page count is known.
    const logo = new Image();
    logo.src = require("@/assets/images/securdia_pdf_logo.png");

    const drawPageChrome = () => {
      // Address block on the left
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(20, 20, 20);
      doc.text("Securdia AB", 15, 12);
      doc.text("Ekbacksvägen 22", 15, 16);
      doc.text("168 69 Bromma", 15, 20);
      // Logo on the right (39.86 × 11.29 mm). Align right edge with content area.
      doc.addImage(logo, "PNG", 195 - 39.86, 8, 39.86, 11.29);
      doc.setTextColor(0);
    };

    // ===== Cover page =====
    drawPageChrome();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(26);
    doc.setTextColor(20, 20, 20);
    const titleLine = agreementNumber
      ? `SAMARBETSAVTAL – ${agreementNumber}`
      : "SAMARBETSAVTAL";
    doc.text(titleLine, 105, 110, { align: "center", maxWidth: 180 });

    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("mellan", 105, 125, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(`Securdia AB & ${customerName}`, 105, 140, {
      align: "center",
      maxWidth: 180,
    });

    doc.setFont("helvetica", "italic");
    doc.setFontSize(13);
    doc.text("Avseende säkerhetsteknik", 105, 158, { align: "center" });

    // ===== Body — start a fresh page for the contract content =====
    doc.addPage();

    // Style helpers for the autoTable body rows. Each helper returns a row
    // (an array containing one cell). They MUST be used without spread, e.g.
    // `bodyPara('text')` — never `...bodyPara('text')`. Spreading would push
    // the raw cell object into the body, and autoTable would render the
    // styles object as a second column → "[object Object]".
    const PARA_PADDING = { top: 1.4, right: 0, bottom: 2.4, left: 0 };
    const SUBLIST_PADDING = { top: 0.6, right: 0, bottom: 0.6, left: 6 };
    const HEADING_PADDING = { top: 6, right: 0, bottom: 1.8, left: 0 };

    const bodyHeading = (text) => [
      {
        content: text,
        styles: {
          fontStyle: "bold",
          fontSize: 11,
          halign: "left",
          cellPadding: HEADING_PADDING,
          textColor: [20, 20, 20],
        },
      },
    ];
    const bodyPara = (text) => [
      {
        content: text,
        styles: {
          fontStyle: "normal",
          fontSize: 9.5,
          halign: "left",
          cellPadding: PARA_PADDING,
        },
      },
    ];
    const bodySublist = (items) =>
      items.map((it) => [
        {
          content: it,
          styles: {
            fontStyle: "normal",
            fontSize: 9.5,
            halign: "left",
            cellPadding: SUBLIST_PADDING,
          },
        },
      ]);

    // Sections 1 — 5 (everything before the price table)
    const preTableBody = [
      [{ content: "SAMARBETSAVTAL", styles: { fontStyle: "bold", fontSize: 13, halign: "left", cellPadding: { top: 0, right: 0, bottom: 3, left: 0 } } }],
      ...secRows('intro', [
        ...paraRow('intro_1'),
        ...paraRow('intro_2'),
        ...paraRow('intro_3'),
        ...paraRow('intro_4'),
        ...paraRow('intro_5'),
      ]),

      ...secRows('s1', [
        secHeading('s1', "1. Bakgrund och syfte"),
        ...paraRow('s1_1'),
        ...paraRow('s1_2'),
        ...paraRow('s1_3'),
        ...paraRow('s1_4'),
      ]),

      ...secRows('s2', [
        secHeading('s2', "2. Avtalets omfattning"),
        ...paraRow('s2_1'),
        ...listRow('s2_1_list', 's2_1'),
        ...paraRow('s2_2'),
        ...paraRow('s2_3'),
        ...paraRow('s2_4'),
        ...paraRow('s2_5'),
      ]),

      ...secRows('s3', [
        secHeading('s3', "3. Avtalshierarki"),
        ...paraRow('s3_1'),
        ...paraRow('s3_2'),
        ...paraRow('s3_3'),
        ...listRow('s3_3_list', 's3_3'),
        ...paraRow('s3_4'),
      ]),

      ...secRows('s4', [
        secHeading('s4', "4. Avtalstid"),
        ...paraRow('s4_1'),
        ...paraRow('s4_2'),
        ...paraRow('s4_3'),
      ]),

      ...secRows('s5', [
        secHeading('s5', "5. Beställningar och Leveransavtal"),
        ...paraRow('s5_1'),
        ...paraRow('s5_2'),
        ...listRow('s5_2_list', 's5_2'),
        ...paraRow('s5_3'),
      ]),

      ...secRows('s6', [
        secHeading('s6', "6. Produkter, tjänster och prislista"),
        ...paraRow('s6_1'),
      ]),
    ];

    doc.autoTable({
      startY: 28,
      margin: { top: 28, bottom: 18, left: 15, right: 15 },
      body: preTableBody,
      theme: "plain",
      tableWidth: 180,
      columnStyles: { 0: { cellWidth: 180 } },
      styles: { overflow: "linebreak", cellPadding: 0 },
      rowPageBreak: "auto",
      didDrawPage: drawPageChrome,
    });

    // ===== Dynamic price table =====
    // Source of truth is `procured_products` — each entry has its own price
    // and purchase_type. `guide_prices` is only used as a fallback when a
    // procured product is missing a price (legacy data).
    const priceRows = (agreement.procured_products || []).map((p) => {
      const isCash = p.purchase_type === "cash";
      const unit = isCash
        ? (isNorwegian ? "stk" : "st")
        : (isNorwegian ? "mnd" : "mån");
      const productName = p.variant_name
        ? `${p.product_name} — ${p.variant_name}`
        : p.product_name;
      // Fallback to guide price if procured price is missing
      let price = p.price;
      if (price == null || price === "") {
        const gp = (agreement.guide_prices || []).find((g) =>
          String(g.product_id || "") === String(p.product_id || "") &&
          String(g.variant_id || "") === String(p.variant_id || "")
        );
        if (gp) price = gp.guide_price;
      }
      return [productName, fmtPrice(price), unit];
    });

    if (priceRows.length === 0) {
      priceRows.push(["—", "—", "—"]);
    }

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 3,
      margin: { top: 28, bottom: 18, left: 15, right: 15 },
      head: [["Artikel", "Pris", "Enhet"]],
      body: priceRows,
      theme: "grid",
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [20, 20, 20],
        fontSize: 9.5,
        fontStyle: "bold",
        halign: "left",
      },
      bodyStyles: {
        fontSize: 9.5,
        cellPadding: 1.5,
      },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "left", cellWidth: 22 },
      },
      didDrawPage: drawPageChrome,
    });

    // Sections 6.2 — 28
    const postTableBody = [
      ...secRows('s6', [
        ...paraRow('s6_2'),
        ...paraRow('s6_3'),
        ...paraRow('s6_4'),
      ]),

      ...secRows('s7', [
        secHeading('s7', "7. Prisjustering"),
        ...paraRow('s7_1'), ...paraRow('s7_2'),
        ...paraRow('s7_4'),
      ]),

      ...secRows('s8', [
        secHeading('s8', "8. Fakturering och betalningsvillkor"),
        ...paraRow('s8_1'), ...paraRow('s8_2'), ...paraRow('s8_3'),
        ...paraRow('s8_4'), ...paraRow('s8_5'),
      ]),

      ...secRows('s9', [
        secHeading('s9', "9. Leverans, installation och ansvarsfördelning"),
        ...paraRow('s9_1'), ...paraRow('s9_2'),
        ...paraRow('s9_3'), ...paraRow('s9_4'),
      ]),

      ...secRows('s10', [
        secHeading('s10', "10. Utrustning, användning och vård"),
        ...paraRow('s10_1'), ...paraRow('s10_2'), ...paraRow('s10_3'),
        ...paraRow('s10_4'), ...paraRow('s10_5'), ...paraRow('s10_6'),
        ...paraRow('s10_7'),
      ]),

      ...secRows('s11', [
        secHeading('s11', "11. Support, service och utbildning"),
        ...paraRow('s11_1'), ...paraRow('s11_2'), ...paraRow('s11_3'),
      ]),

      ...secRows('s12', [
        secHeading('s12', "12. Strategiskt partnerskap och övergångsstöd"),
        ...paraRow('s12_1'), ...paraRow('s12_2'), ...paraRow('s12_3'),
      ]),

      ...secRows('s13', [
        secHeading('s13', "13. Uppsägning av Leveransavtal och omallokering"),
        ...paraRow('s13_1'), ...paraRow('s13_2'), ...paraRow('s13_3'),
        ...paraRow('s13_4'), ...paraRow('s13_5'),
      ]),

      ...secRows('s14', [
        secHeading('s14', "14. Uppsägning av Avtalet i förtid"),
        ...paraRow('s14_1'),
        ...listRow('s14_1_list', 's14_1'),
        ...paraRow('s14_2'),
      ]),

      ...secRows('s15', [
        secHeading('s15', "15. Sekretess"),
        ...paraRow('s15_1'),
        ...paraRow('s15_2'),
        ...listRow('s15_2_list', 's15_2'),
        ...paraRow('s15_3'), ...paraRow('s15_4'), ...paraRow('s15_5'),
      ]),

      ...secRows('s16', [
        secHeading('s16', "16. Personuppgifter och integritet"),
        ...paraRow('s16_1'), ...paraRow('s16_2'), ...paraRow('s16_3'),
      ]),

      ...secRows('s17', [
        secHeading('s17', "17. Immateriella rättigheter"),
        ...paraRow('s17_1'), ...paraRow('s17_2'), ...paraRow('s17_3'),
      ]),

      ...secRows('s18', [
        secHeading('s18', "18. Ansvarsbegränsning"),
        ...paraRow('s18_1'), ...paraRow('s18_2'),
        ...paraRow('s18_3'), ...paraRow('s18_4'),
      ]),

      ...secRows('s19', [
        secHeading('s19', "19. Force majeure"),
        ...paraRow('s19_1'), ...paraRow('s19_2'), ...paraRow('s19_3'),
      ]),

      ...secRows('s20', [
        secHeading('s20', "20. Etik och regelefterlevnad"),
        ...paraRow('s20_1'), ...paraRow('s20_2'), ...paraRow('s20_3'),
      ]),

      ...secRows('s21', [
        secHeading('s21', "21. Offentlig verksamhet och upphandling"),
        ...paraRow('s21_1'), ...paraRow('s21_2'), ...paraRow('s21_3'),
      ]),

      ...secRows('s22', [
        secHeading('s22', "22. Överlåtelse"),
        ...paraRow('s22_1'), ...paraRow('s22_2'), ...paraRow('s22_3'),
      ]),

      ...secRows('s23', [
        secHeading('s23', "23. Meddelanden"),
        ...paraRow('s23_1'),
        ...paraRow('s23_2'),
        ...listRow('s23_2_list', 's23_2'),
      ]),

      ...secRows('s24', [
        secHeading('s24', "24. Kontaktpersoner"),
        ...paraRow('s24_intro'),
      ]),
    ];

    // Sections 25 — 28 (rendered after the dedicated section 24 contact card)
    const tailBody = [
      ...secRows('s24', [
        ...paraRow('s24_post'),
      ]),

      ...secRows('s25', [
        secHeading('s25', "25. Ändringar och tillägg"),
        ...paraRow('s25_1'), ...paraRow('s25_2'),
      ]),

      ...secRows('s26', [
        secHeading('s26', "26. Ogiltighet"),
        ...paraRow('s26_1'), ...paraRow('s26_2'),
      ]),

      ...secRows('s27', [
        secHeading('s27', "27. Fullständig reglering"),
        ...paraRow('s27_1'),
      ]),

      ...secRows('s28', [
        secHeading('s28', "28. Tillämplig lag och tvist"),
        ...paraRow('s28_1'), ...paraRow('s28_2'), ...paraRow('s28_3'),
      ]),
    ];

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 3,
      margin: { top: 28, bottom: 18, left: 15, right: 15 },
      body: postTableBody,
      theme: "plain",
      tableWidth: 180,
      columnStyles: { 0: { cellWidth: 180 } },
      styles: { overflow: "linebreak", cellPadding: 0 },
      rowPageBreak: "auto",
      didDrawPage: drawPageChrome,
    });

    // ===== Section 24 — Contact card (2-column with bold labels) =====
    if (!hidden.has('s24')) {
      const contactRow = (label, value) => [
        {
          content: label,
          styles: {
            fontStyle: "bold",
            fontSize: 9.5,
            cellPadding: { top: 0.6, right: 2, bottom: 0.6, left: 0 },
            cellWidth: 38,
          },
        },
        {
          content: value || "—",
          styles: {
            fontStyle: "normal",
            fontSize: 9.5,
            cellPadding: { top: 0.6, right: 0, bottom: 0.6, left: 0 },
          },
        },
      ];
      const sectionHeader = (text) => [
        {
          content: text,
          colSpan: 2,
          styles: {
            fontStyle: "bold",
            fontSize: 10,
            textColor: [50, 50, 50],
            cellPadding: { top: 3, right: 0, bottom: 1.5, left: 0 },
          },
        },
      ];
      const contactCardBody = [
        sectionHeader("VD Securdia AB"),
        contactRow("Namn:", "Calle Baumgarten"),
        contactRow("E-post:", "Calle@securdia.se"),
        contactRow("Telefon:", "072-971 95 71"),
        sectionHeader("Supportchef Securdia AB"),
        contactRow("Namn:", "Denise Vallberg"),
        contactRow("E-post:", "Denise@securdia.se"),
        contactRow("Telefon:", "079-10 407 37"),
        sectionHeader(`Kundens kontaktperson — ${customerName}`),
        contactRow("Namn:", contactPerson),
        contactRow("E-post:", contactEmail),
        contactRow("Telefon:", contactPhone),
      ];

      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 2,
        margin: { top: 28, bottom: 18, left: 15, right: 15 },
        body: contactCardBody,
        theme: "plain",
        tableWidth: 180,
        columnStyles: { 0: { cellWidth: 38 }, 1: { cellWidth: 142 } },
        styles: { overflow: "linebreak", cellPadding: 0 },
        rowPageBreak: "auto",
        didDrawPage: drawPageChrome,
      });
    }

    // ===== Sections 25 — 28 (after the contact card) =====
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 3,
      margin: { top: 28, bottom: 18, left: 15, right: 15 },
      body: tailBody,
      theme: "plain",
      tableWidth: 180,
      columnStyles: { 0: { cellWidth: 180 } },
      styles: { overflow: "linebreak", cellPadding: 0 },
      rowPageBreak: "auto",
      didDrawPage: drawPageChrome,
    });

    // ===== Custom sections (appended before signatures) =====
    // Support new custom_sections array and legacy special_conditions string.
    let customSections = Array.isArray(agreement.custom_sections) ? agreement.custom_sections : [];
    if (customSections.length === 0 && (agreement.special_conditions || '').trim()) {
      customSections = [{ heading: 'Tillägg och särskilda villkor', content: agreement.special_conditions }];
    }
    for (const section of customSections) {
      const heading = (section.heading || '').trim();
      const content = (section.content || '').trim();
      if (!heading && !content) continue;
      const sectionBody = [];
      if (heading) sectionBody.push(bodyHeading(heading));
      if (content) content.split('\n').filter(Boolean).forEach(line => sectionBody.push(bodyPara(line)));
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 3,
        margin: { top: 28, bottom: 18, left: 15, right: 15 },
        body: sectionBody,
        theme: 'plain',
        tableWidth: 180,
        columnStyles: { 0: { cellWidth: 180 } },
        styles: { overflow: 'linebreak', cellPadding: 0 },
        rowPageBreak: 'auto',
        didDrawPage: drawPageChrome,
      });
    }

    // ===== Signatures (kept on a fresh page for layout stability) =====
    doc.addPage();
    drawPageChrome();

    let sigY = 30;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text("29. Underskrifter", 15, sigY);
    sigY += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const signatureIntro =
      "Detta Avtal har upprättats i två (2) exemplar, varav Parterna tagit var sitt, eller undertecknats elektroniskt.";
    const introLines = doc.splitTextToSize(signatureIntro, 180);
    doc.text(introLines, 15, sigY);
    sigY += introLines.length * 5 + 8;

    // Two-column signature block
    const colLeftX = 15;
    const colRightX = 110;
    const colWidth = 85;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("För Securdia AB", colLeftX, sigY);
    const rightHeading = doc.splitTextToSize(
      `För ${customerName}`,
      colWidth
    );
    doc.text(rightHeading, colRightX, sigY);
    sigY += Math.max(8, rightHeading.length * 5 + 3);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);

    const signatureLines = [
      "Ort och datum: ________________________",
      "Namn: ________________________",
      "Underskrift: ________________________",
    ];
    signatureLines.forEach((line) => {
      doc.text(line, colLeftX, sigY);
      doc.text(line, colRightX, sigY);
      sigY += 12;
    });

    // ===== Page numbers (rendered last so total page count is final).
    // Cover page (page 1) is skipped — only content pages get a number. =====
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 2; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(`Sida ${i - 1} av ${totalPages - 1}`, 195, 290, { align: "right" });
      if (agreementNumber) {
        doc.text(agreementNumber, 15, 290);
      }
      doc.setTextColor(0);
    }

    return doc;
  },

  checkIOS() {
    const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
    this.isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    return this.isIOS;
  },
  getMapLink(mapType, gps_pos) {
    if (mapType === "google") {
      return `https://www.google.com/maps?saddr=My+Location&daddr=${gps_pos.lat},${gps_pos.lng}`;
    }

    if (mapType === "apple") {
      return `http://maps.apple.com/?saddr=Current+Location&daddr=${gps_pos.lat},${gps_pos.lng}`;
    }
  },
};
